/* ═══════════════════════════════════════════════════════════════════════════════════════════════════════════
   THE BUILDER, DIRECTION B — "STEP BY STEP". Phase 0 (2026-10-08): the switch and the step skeleton. Phase 1
   (2026-10-09): the phone's steps 1-6 as drawn, the two keyboard states, and step 3 per design.

   Daniel chose Claude Design's direction B on 8 Oct. The plan, the design as drawn and the order to build it in:
   projects/pop-cards/docs/plans/2026-10-08-builder-b-plan.md (venture repo). Loaded by the `ppb` branch in
   sections/product-builder.liquid: since the switch-over (2026-10-09) the builder's plain address and ?view=builder-b;
   never today's builder, which is at ?view=builder-classic.

   B IS NEW CHROME ON THE SAME COMPONENT. PP.builderB() builds today's PP.configurator() and adds the step
   controller to it, so the photos, the 3D card, the inside top, the note, the draft (pp_draft_v3, never
   version-bumped), the cart properties and every analytics event are today's code, unchanged. B's `ppbStep` is
   not saved: sections are not, on purpose. Every control in B's tray calls today's function for it.

   THE ONE IDEA: the step DRIVES today's `section` (ppbGo), and FOLLOWS it when something else moves it (a tap on
   the note or the inside top on the card, a picture tapped from Look, Preview's way into Send). Two steps share a
   section — Photos (revisited in the builder) and Look are both the whole card, 'card' — which is the only reason
   the step is a field of its own and not derived.

     step       section      camera (camMode)                      tray
     photos     card         (the page covers it)                  none: the step 1 page, builder-b-photos
     pictures   photos       the four pictures; one alone on a     Filter · Crop · Move · Replace
                             phone once one is selected
     top        inside*      the inside top (CoverTop)             per design: Style | Words | Photo, As designed |
                                                                   Photo, or As designed alone (*'card' on keep)
     note       bottom       the note (CoverBottom)                the three letterings
     look       card         the whole card                        border, corners, Shadow
     preview    preview      today's Preview                       none; Open/Closed in the header or on the stage
     send       recipients   (no stage)                            none: today's "Who's it for?" (phase 3)

   STEP 3 PER DESIGN (decisions.md 2026-10-08, "Every card's inside top"; the artists catalogue's topChoice, built
   from a reviewed CSV so the list changes without code):
     words  a text-free top (CoverTopPlain): as designed, the customer's words, or their photo. Today's choice.
     photo  as designed or the customer's photo over the panel above the glue band. B turns hasTopChoice() on for
            these, and keeps the order identical to today's when the customer keeps the design (no `_Top: design`,
            which today's builder never sends on them). Print draws the photo over the design's own top.png.
     keep   as designed only: the inside top completes the cover. hasTopChoice() stays off.
   Words are never printed over art.

   THE KEYBOARD (a phone typing the inside top's words or the note): `ppbKb` is set while one of the card's own
   fields has the focus; header, stepper, tray and bar go (builder-b.css, [data-ppb-kb]), the camera frames the one
   panel and the other planes fade, and the keyboard bar is the only control. Exactly one thing answers the keyboard,
   and it is today's shell (kbBoxH / kbBoxStyle); nothing here measures it.

   What B overrides on the component, and why (each is a method today's code calls through `this`):
     · camMode / framingBoxes  the step framings above. Today's 'photos' mode is a sliding window that pages one
                               picture at a time on a phone; B has no paging strip.
     · uiInset                 0. B's tray and bar are in flow under the stage, so nothing floats over the card
                               and the card never re-frames because a tool opened.
     · leaveToFullCard         a click on empty stage, or Escape, puts a selected picture down and nothing else.
     · landInBuilder           B lands on Pictures (step 2) after the opening, not the whole card.
     · _armToolbarHint         off: B has no "Start here".
     · noteOnCard              true: the note is typed on the card on a phone too, never in today's sheet.
     · hasTopChoice, topOnDesign, _topLoadBase, buildCartProperties   the photo designs, above.
   ═══════════════════════════════════════════════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';
  window.PP = window.PP || {};
  if (typeof window.PP.configurator !== 'function') return;   // the section falls back to PP.configurator

  /* FIVE STEPS SINCE 19 OCT (Daniel and Yonatan's sync: "merge Look and Preview, merge Photos and Pictures"): Photos is
     the photo page until the card exists and the four pictures' tools after it (the step keyed 'pictures'), and Look
     has Preview's Open / Closed and goes straight to Send, the envelope included. ?ppsteps7=1 brings back the seven
     in this browser, ?ppsteps7=0 the five. */
  const SEVEN = (function () {
    try {
      const m = /[?&]ppsteps7=(0|1)(?:&|$)/.exec(location.search);
      if (m) { if (m[1] === '1') localStorage.setItem('pp_ppsteps7', '1'); else localStorage.removeItem('pp_ppsteps7'); }
      return localStorage.getItem('pp_ppsteps7') === '1';
    } catch (e) { return /[?&]ppsteps7=1(?:&|$)/.test(location.search); }
  })();
  /* ACHINOAM'S FIXES (?ppachi=1, snippets/pp-achi-switch.liquid; Daniel, 22 Oct: "behind a switch so I can decide"):
     the inside top's step is "Top note", and the picture tools show greyed before a picture is picked. */
  const ACHI = document.documentElement.getAttribute('data-ppachi') === '1';
  const TOP_LABEL = ACHI ? 'Top note' : 'Inside';
  const STEPS = SEVEN ? [
    { key: 'photos', label: 'Photos' },
    { key: 'pictures', label: 'Pictures' },
    { key: 'top', label: TOP_LABEL },   // 'Inside top' until 19 Oct (Yonatan: one-word step names)
    { key: 'note', label: 'Note' },
    { key: 'look', label: 'Look' },
    { key: 'preview', label: 'Preview' },
    { key: 'send', label: 'Send' },
  ] : [
    { key: 'pictures', label: 'Photos' },
    { key: 'top', label: TOP_LABEL },
    { key: 'note', label: 'Note' },
    { key: 'look', label: 'Look' },
    { key: 'send', label: 'Send' },
  ];
  // The step Look's Open / Closed and the envelope stand on: Preview in the seven, Look in the five.
  const SHOW_STEP = SEVEN ? 'preview' : 'look';

  // configurator.js PANELS, in the same order. The four picture planes of the artist's card.
  const PANELS = ['OutLeft1', 'InLeft1', 'InRight1', 'OutRight1'];

  // The section each step stands on, and the step each section belongs to.
  const STEP_SECTION = { photos: 'card', pictures: 'photos', top: 'inside', note: 'bottom', look: 'card', preview: 'preview', send: 'recipients' };
  const SECTION_STEP = { photos: 'pictures', inside: 'top', bottom: 'note', preview: SEVEN ? 'preview' : 'look', recipients: 'send' };

  // Today's cardTool each step keeps set. Nothing of today's popover shows in B any more (phase 1 drew the bodies);
  // the state stays because today's code reads it: the template tiles render only while 'topstyle' is the tool.
  const STEP_TOOL = { top: 'topstyle', note: 'font', look: 'border' };

  /* THE SIX LOOKS, as drawn (plan §7, answer 1, Daniel 8 Oct): Deep is today's punch, and cool leaves the picker.
     A picture or a draft that already has cool keeps it and prints it; no swatch is ticked for it. No new look:
     a new look needs its print twin first. */
  // 'pop' (12 Oct): the gentle lift new photos get by default, so its swatch is ticked on them.
  const LOOKS = [['original', 'Original'], ['pop', 'Pop'], ['warm', 'Warm'], ['bright', 'Bright'], ['faded', 'Faded'], ['mono', 'Mono'], ['punch', 'Deep']];

  // The design's two devices. Not photosPerPage(): a laptop whose stage is short would count as a phone there,
  // and B's desktop keeps all four pictures in view whatever the window.
  const PHONE_MQ = '(max-width: 768px)';
  /* ── THE SIDE PANEL ON A SHORT WINDOW (Daniel, 18 Oct, Grandma's laptop: "This is how it looks on her laptop").
     The desktop's tray and bar are fixed heights under the card (402px on Look), so a laptop window 550-650px tall,
     which is most Windows laptops at 100% with a bookmarks bar, left the card 40-70px tall. On a window this short and
     wide enough, the tray and Back/Next go to a column on the right and the card gets the whole height under the
     stepper. ?ppside=1 has it on any desktop window, ?ppside=0 never (remembered like the other pp* switches); the
     address without it is the rule below. THE COLUMN IS ON THE LEFT (Daniel, 18 Oct, after comparing both: "Make the
     left the default"); ?ppside=right has it on any desktop window with the column on the right, the first version,
     and ?ppside=left is the same as 1. Read once, at load. */
  /* EVERY DESKTOP WINDOW SINCE 19 OCT (Daniel and Yonatan's sync: "we'll switch to a sidebar"; it keeps the card's
     height on the web). Until then only a window this short: '(min-width: 900px) and (max-height: 760px)'. ?ppside=0
     still brings back the tray under the card. A phone keeps its tray at the bottom. */
  const SIDE_MQ = '(min-width: 900px)';
  const SIDE_SWITCH = (function () {
    try {
      const m = /[?&]ppside=(0|1|left|right|auto)(?:&|$)/.exec(location.search);
      if (m) { if (m[1] === 'auto') localStorage.removeItem('pp_ppside'); else localStorage.setItem('pp_ppside', m[1]); }
      return localStorage.getItem('pp_ppside') || 'auto';
    } catch (e) { const m = /[?&]ppside=(0|1|left|right)(?:&|$)/.exec(location.search); return m ? m[1] : 'auto'; }
  })();
  /* THE INSIDE TOP'S LETTERING AND COLOUR MENUS: off since 19 Oct (Daniel and Yonatan's sync: "drop the lettering, drop
     the colour, keep about five layouts"). A layout keeps its own (setTopTemplate clears both); a draft that chose
     others keeps them and prints them. ?pptoplook=1 brings the menus back in this browser, ?pptoplook=0 hides them. */
  const TOP_LOOK = (function () {
    try {
      const m = /[?&]pptoplook=(0|1)(?:&|$)/.exec(location.search);
      if (m) { if (m[1] === '1') localStorage.setItem('pp_pptoplook', '1'); else localStorage.removeItem('pp_pptoplook'); }
      return localStorage.getItem('pp_pptoplook') === '1';
    } catch (e) { return /[?&]pptoplook=1(?:&|$)/.test(location.search); }
  })();
  /* ✂ "REMOVE BACKGROUND" (Daniel and Yonatan's 19 Oct sync). ON FOR EVERYONE since 19 Oct (Daniel: "I already asked not
     to put them behind switches but to put them live"); ?ppbg=0 turns it off in this browser, ?ppbg=1 back on
     (configurator.js rememberSwitches carries it like the others). The person is found by
     PPVision.person (assets/pp-vision.js), the mask goes on the picture (configurator.js cutMaskCss) so the strip's
     art shows behind them, and the bake draws that art behind them for print (bakeCut). The cut lives with the page,
     not the draft: a reload brings the whole picture back. Why it was off until then: projects/pop-cards/decisions.md, 19 Oct. */
  const BG_TRY = (function () {
    try {
      const m = /[?&]ppbg=(0|1)(?:&|$)/.exec(location.search);
      if (m) { if (m[1] === '0') localStorage.setItem('pp_ppbg', '0'); else localStorage.removeItem('pp_ppbg'); }
      return localStorage.getItem('pp_ppbg') !== '0';
    } catch (e) { return !/[?&]ppbg=0(?:&|$)/.test(location.search); }
  })();
  const PIC_HEADROOM = 0.18;
  const LOOKS_KEPT = 12;       // looks a picture remembers for Undo (photo.looks, in the draft)
  /* The glue band on each half of the cover sheet, as a share of the plane: half the 80.5 mm band (40.25 mm) from the
     fold, on today's 122.5 mm planes (stage.css .pp-cardnote: the builder's 3D card keeps that spec until the 3D
     artist's model). The note shows below it; the inside top above it. */
  const GLUE_SHARE = (69 / 2) / 118.7;   // 3:4 strip (Yonatan, 22 Oct): half its 69 mm, on the 118.7 mm page (v2e 71.155; was 40.25 / 122.5)
  const FLAT_PAD = 0.92;       // of the stage, round a panel shown flat
  // A swipe between pictures: this far sideways, mostly sideways, and not slow.
  const SWIPE_PX = 48, SWIPE_MS = 700;   // of a panel's height, over a picture framed alone (framingBoxes 'pic')

  function blurActive() {
    const a = document.activeElement;
    if (a && a !== document.body && typeof a.blur === 'function') a.blur();
  }
  // The card's own fields: typing in one of these is the keyboard state.
  function kbFieldOf(el) {
    if (!el || !el.matches) return null;
    if (el.matches('[data-pp-note-field]')) return 'note';
    if (el.matches('[data-ppb-top]')) return 'top';
    if (el.matches('[data-ppb-send-field]')) return 'send';
    return null;
  }

  // configurator.js's one way to report an event (pp_experiments and debug_mode ride along there).
  function track(name, props) { try { if (window.PP && window.PP.track) window.PP.track(name, props); } catch (e) { /* never stops the page */ } }

  /* ── THE PEOPLE, MANY OF THEM (Daniel, 9 Oct: "remove limits") ───────────────────────────────────────────────
     Up to PEOPLE_FOLD people the list is every person, folded to their envelope line. Past it the list shows that
     many, with a search, "Only the ones to fix", and Show all. An imported list shows IMPORT_SHOWN rows to fix at a
     time, for the same reason: the page must stay quick at 200. */
  const PEOPLE_FOLD = 8;
  const IMPORT_SHOWN = 20;
  let impSeq = 0;

  /* ── INTO THE CARD, BEFORE ALPINE ─────────────────────────────────────────────────────────────────────────
     builder-b-tray.liquid renders the inside top's guides and fields, and the note's outline, beside the stage;
     they belong ON the planes, under the camera, so they move into them here. It runs NOW, at script time: this
     file is deferred and sits before the layout's alpine.min.js, so the document is parsed and Alpine has not
     walked it. Moving a node once Alpine had initialised the tree would leave its bindings attached to a node in
     the wrong place (phase 0 moved today's popover the same way). */
  (function graft() {
    if (!document.querySelector('[data-builder-ui="b"]')) return;
    document.querySelectorAll('[data-ppb-graft]').forEach(function (g) {
      const plane = document.querySelector('[data-pp-plane="' + g.getAttribute('data-ppb-graft') + '"]');
      if (plane) plane.appendChild(g);
    });
  })();

  function builderB(variantId, checkoutBaseUrl) {
    const c = window.PP.configurator(variantId, checkoutBaseUrl);
    const base = {
      init: c.init,
      camMode: c.camMode,
      framingBoxes: c.framingBoxes,
      uiInset: c.uiInset,
      landInBuilder: c.landInBuilder,
      hasTopChoice: c.hasTopChoice,
      topOnDesign: c.topOnDesign,
      _topLoadBase: c._topLoadBase,
      buildCartProperties: c.buildCartProperties,
    };

    Object.assign(c, {
      ppbStep: 'pictures',
      ppbPhone: false,
      ppbSideFits: false,     // the window is short and wide enough for the side panel (SIDE_MQ)
      ppbPicPanel: null,      // 'filter' | 'move' — B's own picture panels; Crop is today's editSub
                              // (+ 'straighten' with photo framing B1, ?ppframe=b1)
      ppfButtons: false,      // B1: Move with buttons open (the four arrows under Zoom)
      ppbKb: null,            // 'top' | 'note' | 'send' while a phone types on the card or the Send page
      ppbChat: false,         // Shopify's chat component is there: the header's chat icon shows
      ppbKbField: 'title',    // the inside top's line being typed
      ppbSheet: null,         // 'layout' | 'face' | 'colour'
      ppbWordsPick: false,    // the Layout menu is open to choose the layout the customer's own words go in (#7)
      ppbTopTab: 'style',     // 'style' | 'words' | 'photo' | 'design'
      ppbTopFocus: null,      // 'title' | 'subtitle' while one of the inside top's fields has the focus, on the card or in the tray
      ppbSendForm: 'people',  // Send, keyboard up: the form being typed in, 'from' (From you) or 'people'
      ppbOccOpen: false,      // the occasion menu (phase 5): the header's occasion on desktop, Change on step 1
      _ppbOccAt: null,        // where the desktop menu hangs: under the header's occasion
      _ppbExtra: [],          // people put aside when Several became one person, given back if it is Several again
      ppbPeopleQ: '',         // Several, a long list: the search
      ppbPeopleAll: false,    // ... Show all
      ppbPeopleTodo: false,   // ... only the ones still to finish
      ppbImpOpen: false,      // the Add a list panel (paste or a file)
      ppbImpText: '',         // what was pasted
      ppbImpErr: '',
      ppbImpBusy: false,
      ppbImp: null,           // the list being looked over: { rows, src, name, total }, see ppbImpLoad
      ppbImpAll: false,       // every row to fix, not the first IMPORT_SHOWN
      ppbImpReadyOpen: false, // the ready rows, listed

      ppbZoomPref: null,      // this step's zoom, while on it: null = the step's own, 'out' = the whole card, 'in' = Look close
      ppbZoomCue: false,      // the button's one-time "look at me", the first time each step opens

      _ppbLanded: false,
      _ppbReplaceAt: null,
      ppbCutBusy: null,       // the picture (its pixels) being cut out, while PPVision looks
      ppbCutSaid: '',         // the last cut-out's answer, when there was no one to cut out
      _ppbKbT: 0,
      _ppbSendEl: null,       // the Send field last typed in, kept in place when the keyboard closes
      _topFull: {},           // configurator.js topInput's refusals, declared here so the notice is reactive

      // ── The steps ──────────────────────────────────────────────────────────────────────────────────────
      ppbSteps() { return STEPS; },
      ppbTopLookOn() { return TOP_LOOK; },
      ppbHas(key) { return STEPS.some(function (s) { return s.key === key; }); },
      // Step 1 before the card exists (today's entry step 'photos'), else the builder's step.
      ppbCurStep() { return this.stage === 'builder' ? this.ppbStep : (SEVEN ? 'photos' : 'pictures'); },
      ppbShowStep() { return SHOW_STEP; },
      ppbStepAttr() {
        if (this.stage === 'builder') return this.ppbStep;
        return this.ppbPhotosPage() ? 'entry-photos' : 'entry';
      },
      // The section a step stands on: Inside top is 'card' on a design whose inside top is as designed only.
      ppbSectionOf(key) {
        if (key === 'top' && !this.hasTopChoice()) return 'card';
        return STEP_SECTION[key];
      },
      ppbIndex() {
        const key = this.ppbCurStep();
        return Math.max(0, this.ppbSteps().findIndex(function (s) { return s.key === key; }));
      },
      ppbLabel(key) {
        const s = STEPS.find(function (x) { return x.key === key; });
        return s ? s.label : '';
      },
      ppbPrev() { const s = this.ppbSteps()[this.ppbIndex() - 1]; return s ? s.key : null; },
      ppbNext() { const s = this.ppbSteps()[this.ppbIndex() + 1]; return s ? s.key : null; },
      ppbNextLabel() {
        const n = this.ppbNext();
        if (!n) return '';
        return 'Next: ' + (n === 'send' ? 'who it’s for' : this.ppbLabel(n).toLowerCase());
      },
      ppbBackLabel() {
        const p = this.ppbPrev();
        if (!p) return '';
        return this.ppbPhone ? 'Back' : 'Back: ' + this.ppbLabel(p);
      },
      ppbMid() {
        return this.ppbLabel(this.ppbStep) + ' · step ' + (this.ppbIndex() + 1) + ' of ' + this.ppbSteps().length;
      },
      // Steps 1 and 7 are pages in the design, Preview has a bar and no tray. Zoomed out to the whole card, an editing
      // step loses its tray too and looks like Preview (Look keeps its own: its tools are the whole card's).
      // The side panel (SIDE_MQ): only while a tray is up, so Preview and a zoomed-out step keep the whole stage.
      /* ⭐ WITH THE SIDE PANEL, ZOOM AND CLOSED NEVER TAKE THE TOOLS AWAY (Daniel, 19 Oct: "When I click Zoom Out we need
         to stay in sidebar view. At the moment it zooms out and puts the bottom bar in view"). Hiding the tray was how
         the tray under the card gave its height back; a column beside the card takes none, so the column stays and only
         the camera moves. A phone, and ?ppside=0, keep the rule above. */
      ppbSideWanted() {
        if (this.ppbPhone || SIDE_SWITCH === '0') return false;
        return SIDE_SWITCH === '1' || SIDE_SWITCH === 'left' || SIDE_SWITCH === 'right' || this.ppbSideFits;
      },
      ppbSideOn() { return this.ppbSideWanted() && this.ppbTrayOn(); },
      // On the right by default since 25 Oct (Daniel: "build it", after people missed the left panel and its Next);
      // ?ppside=left keeps the 18 Oct left column in this browser.
      ppbSideLeft() { return SIDE_SWITCH === 'left' && this.ppbSideOn(); },
      ppbTrayOn() {
        if (this.stage !== 'builder' || this.ppbKb || ['pictures', 'top', 'note', 'look'].indexOf(this.ppbStep) === -1) return false;
        if (this.ppbSideWanted()) return true;
        // Look keeps its tray shut or open since 24 Oct: its views (Open, Closed, Back, Spin) are in it (card-views.js).
        return !(this.ppbWhole() && this.ppbStep !== 'look');
      },

      /* ── ZOOM IN AND OUT ON EVERY EDITING STEP (Daniel, 11 Oct) ───────────────────────────────────────────────
         "I need to add a function where the card can be zoomed in and out in every step of the builder ... Zoom out
         needs to show the whole card ... we need to lose the big space, lose the current toolbar, so that it looks
         like the preview ... In the look step it's already zoomed out but quite small; I need the option to zoom in
         on the border (Part B)."
         The control (Daniel, 11 Oct, after four options and three prominence variants): one white pill on the stage's
         lower right, a magnifier and "Zoom out" / "Zoom in", with one soft pulse the first time each step opens. On
         Look, whose own view is the whole card, it starts as "Zoom in". For everyone since the same day.
           close-up    the step's own framing (Pictures: the four pictures; Inside top; Note; Look: Part B, the
                       pictures on their border, new)
           whole card  the camera's 'card' framing, the tray gone (not on Look), the header, stepper and bar kept
         The zoom is the step's only while the visitor is on it: moving to another step starts at that step's own.
         Editing while zoomed out zooms back in: a picture tapped on the whole card is selected and its tools come
         back; typing on the inside top or the note does the same. Nothing is refused. */
      /* Not on a phone's Pictures (Daniel, 12 Oct: "we don't need to zoom out if it's not zoomed in yet"): the four
         pictures are the card's widest part, so framing them on a phone already shows the whole card, and Zoom out
         moved nothing. */
      /* ...and back on a phone's Photos since 19 Oct (Daniel: "When I'm on photos, I don't have a zoom out button. I have
         an all pictures button, which isn't clear. I'd rather have the zoom out button"). Zoom out there puts a selected
         picture down and shows the whole card, inside top and note included; the phone's All pictures chip is gone. */
      ppbZoomStep() {
        return ['pictures', 'top', 'note', 'look'].indexOf(this.ppbStep) !== -1;
      },
      ppbZoomable() {
        return this.stage === 'builder' && !this.ppbKb && !this.foldedInEditor && this.ppbZoomStep();
      },
      ppbWhole() {
        if (!this.ppbZoomable()) return this.ppbStep === 'look';
        return this.ppbStep === 'look' ? this.ppbZoomPref !== 'in' : this.ppbZoomPref === 'out';
      },
      ppbZoomSet(out, how) {
        if (!this.ppbZoomable() || out === this.ppbWhole()) return;
        if (out) {
          blurActive();
          this.ppbSheet = null;
          if (this.selectedPhoto !== null) this.closePhoto();
          if (this.editSub !== 'none') this.editSub = 'none';
          this.ppbPicPanel = null;
        }
        this.ppbZoomPref = this.ppbStep === 'look' ? (out ? null : 'in') : (out ? 'out' : null);
        this.ppbZoomCue = false;
        const self = this;
        this.$nextTick(function () { self.camNonce++; });      // the stage grew or shrank with the tray
      },
      ppbZoomToggle(how) { this.ppbZoomSet(!this.ppbWhole(), how); },
      // The button's word (Daniel, 11 Oct: "Zoom out", and "Zoom in" to mirror it; not the step's part).
      ppbZoomWord() { return this.ppbWhole() ? 'Zoom in' : 'Zoom out'; },
      ppbZoomAria() {
        if (!this.ppbWhole()) return 'Zoom out to the whole card';
        return { pictures: 'Zoom in on the pictures', top: 'Zoom in on the inside top', note: 'Zoom in on the note', look: 'Zoom in on the border' }[this.ppbStep] || 'Zoom in';
      },
      /* The cue: the first time each step opens in a visit, once the card is open and still, for CUE_MS. The CSS
         plays it (builder-b.css .ppb-zoombtn.is-cue) and drops the motion under prefers-reduced-motion. */
      _ppbZoomCueArm(n) {
        const self = this;
        if ((this.opening || this.closing || !this._openingDecided) && (n || 0) < 30) { setTimeout(function () { self._ppbZoomCueArm((n || 0) + 1); }, 400); return; }
        if (!this.ppbZoomable()) return;
        const seen = this._ppbCueSeen || (this._ppbCueSeen = {});
        if (seen[this.ppbStep]) return;
        seen[this.ppbStep] = true;
        this.ppbZoomCue = true;
        clearTimeout(this._ppbCueT);
        this._ppbCueT = setTimeout(function () { self.ppbZoomCue = false; }, 2400);
      },
      ppbBarOn() { return this.stage === 'builder' && !this.ppbKb && this.ppbStep !== 'send'; },

      /* Go to a step: the step first, then today's section, so the section watcher (ppbFollow) finds the step
         already where it belongs. Whatever was being typed lets go first, and a menu sheet closes. */
      ppbGo(key) {
        if (this.stage !== 'builder' || !key || !this.ppbHas(key)) return;
        blurActive();
        this.ppbKb = null;
        this.ppbSheet = null;
        if (this.noteSheetOpen && typeof this.closeNoteSheet === 'function') this.closeNoteSheet();
        this.ppbStep = key;
        if (key === 'top' && this.hasTopChoice()) {
          if (this.ppbTopKind() === 'words') this.openInside();   // section 'inside', 'topstyle', faces loaded
          else this.setSection('inside');
          this.ppbTopTab = this.topMode() === 'photo' ? 'photo' : (this.ppbTopKind() === 'photo' ? 'design' : 'style');
        } else if (key === 'send' && this.org) {
          /* ⭐ ORG MODE (14 Oct): nobody is typed here. The order's people, and each one's address, are the business app's
             list; it fills them in at order time (business-worker cards.js drops every address property the builder
             could send). The page says whose card this is and saves it (configurator.js saveOrgCard). */
          this.setSection('recipients');
        } else if (key === 'send') {
          this.openRecipients();                            // seeds the first person, as Preview's button does
          if (!this.sendTo) this.sendTo = 'other';          // configurator.js state, kept in the draft (phase 4)
          if (this.recipients.length > 1) this.sendTo = 'several';
          /* A set's one choice (To me): through ppbSendGo, which keeps one person and fills in their own address. */
          const set = window.PPSet && window.PPSet.active();
          if (set && set.sendTo && !this.artist && this.sendTo !== set.sendTo) this.ppbSendGo(set.sendTo);
          if (this.sendTo !== 'several') this.recipOpen = 0;
        }
        else this.setSection(this.ppbSectionOf(key));
        this.ppbTool();
        this.camNonce++;
      },

      // Something else moved the section: the step follows it. 'card' keeps a step that stands on it, else Look.
      ppbFollow(section) {
        let next = SECTION_STEP[section] || null;
        if (section === 'card') next = (this.ppbSectionOf(this.ppbStep) === 'card') ? this.ppbStep : 'look';
        if (next && next !== this.ppbStep && this.ppbHas(next)) this.ppbStep = next;
        this.ppbTool();
      },

      ppbTool() {
        if (this.stage !== 'builder') return;
        const want = STEP_TOOL[this.ppbStep];
        if (!want) return;
        /* ⚠️ Only once the section has caught up. A picture tapped on the card from Look runs clearTools() and
           then moves the section in the same turn; the cardTool watcher can run before the section watcher has
           moved the step on. */
        if (this.ppbSectionOf(this.ppbStep) !== this.section) return;
        if (this.ppbStep === 'top') {
          if (this.ppbTopKind() === 'words' && this.cardTool !== 'topstyle') { this.cardTool = 'topstyle'; this.topThumbsSoon(); }
          return;
        }
        if (this.cardTool !== want) { this.cardTool = want; this.editSub = 'none'; }
      },

      /* B lands on Pictures, once per entry into the builder stage. landInBuilder is called before the unfold
         (openFromStanding) and on the routes that skip the opening, so the tray is built against a card that is
         standing still, and the camera, frozen through the opening, glides to the pictures when it is released. */
      ppbLand() {
        if (this._ppbLanded || this.stage !== 'builder') return;
        this._ppbLanded = true;
        this.ppbGo('pictures');
        this._ppbZoomCueArm();
      },

      // ── Step 1, Photos ─────────────────────────────────────────────────────────────────────────────────
      ppbPhotosPage() {
        if (this.stage === 'builder') return this.ppbStep === 'photos';
        // An artist's free card and a business's card (org mode) are B's too since 14 Oct (configurator.js noOldEntry).
        return this.stage === 'entry' && typeof this.isEntryStep === 'function' && this.isEntryStep('photos');
      },
      // "Pick 4 photos" / "Add 2 more photos" / "Add one more photo": today's photoTitle(), which also says a refusal.
      // With four, a title of its own (the revisit is not drawn; plan §3).
      ppbPhotosTitle() {
        if (this.photos.length >= 4 && !(this.errors && this.errors._photoLimit)) return 'Your four photos';
        return this.photoTitle();
      },
      ppbForLine() {
        // Org mode: which card of the order this is ("Everyone’s card", "Dana’s card"), as the business app names it.
        if (this.org) return this.org.label || 'Your card';
        const occ = this.card.occasion || this.occasion || '';
        const o = (window.PP.OCCASIONS || []).filter(function (x) { return x.slug === occ; })[0];
        return o && o.phrase ? 'For ' + o.phrase : 'For any occasion';
      },

      // ── Pictures ───────────────────────────────────────────────────────────────────────────────────────
      ppbThumb(i) {                                  // tap a thumbnail to select; tap it again to put it down
        if (this.selectedPhoto === i) { this.closePhoto(); return; }
        this.focusPhoto(i);
      },
      ppbPick(d) {                                   // ← Picture 2 of 4 →, round the four
        const n = this.photos.length;
        if (this.selectedPhoto === null || !n) return;
        this.focusPhoto((this.selectedPhoto + d + n) % n);
      },
      ppbPicOn(name) {
        if (name === 'crop') return this.editSub === 'crop';
        return this.ppbPicPanel === name && this.editSub !== 'crop';
      },
      ppbPicTool(name) {
        if (this.selectedPhoto === null) return;
        if (name === 'crop') {
          this.ppbPicPanel = null;
          this.setEditSub(this.editSub === 'crop' ? 'none' : 'crop');
          return;
        }
        if (this.editSub !== 'none') this.setEditSub('none');
        this.ppbPicPanel = this.ppbPicPanel === name ? null : name;
        if (this.ppbPicPanel) this.ppfReveal('.ppb-t--main.ppb-' + (name === 'straighten' ? 'straighten' : name));
      },
      /* B1 on a phone: the Zoom row stays under the tools, so a panel opened below it can start under the tray's fold
         (the tray scrolls; it never grows, so the card does not move). Bring what was opened into view. */
      ppfReveal(sel) {
        if (!this.frameB1() || !this.ppbPhone) return;
        this.$nextTick(function () {
          const el = document.querySelector('.ppb-tray ' + sel);
          if (el && el.offsetParent) { try { el.scrollIntoView({ block: 'nearest', behavior: 'auto' }); } catch (e) { /* stays */ } }
        });
      },
      /* The selection follows the picture (_applyPhotoOrder), and Move stays open on it in its new place, so it can be
         swapped again (Daniel, 9 Oct: "after moving a pic left/right, don't hide the move buttons"). The selection
         watcher closes B's panels when the customer picks another picture; a swap is not that. */
      ppbSwap(d) {
        const i = this.selectedPhoto;
        if (i === null) return;
        const j = i + d;
        if (j < 0 || j >= this.photos.length) return;
        this._ppbSwapping = true;
        this.movePicture(i, j);
        const self = this;
        setTimeout(function () { self._ppbSwapping = false; self.ppbPicPanel = 'move'; }, 0);
      },
      // ⚠️ The file input is clicked synchronously inside the tap, or iOS opens no picker.
      ppbReplaceAt(i) {
        this._ppbReplaceAt = i;
        if (this.$refs.ppbReplace) this.$refs.ppbReplace.click();
      },
      // ✂ Remove background (BG_TRY): cut the person out, or put the background back.
      ppbBgOn() { return BG_TRY && !!window.PPVision; },
      ppbAchi() { return ACHI; },
      // A greyed tool, before a picture is picked (ACHI): picks the first picture, so the real tools come up.
      ppbIdleTool() { if (this.selectedPhoto === null && this.photos.length) this.focusPhoto(0); },
      ppbCutOn() { const p = this.selectedPhoto === null ? null : this.photos[this.selectedPhoto]; return !!(p && p.cutUrl); },
      ppbCutting() { const p = this.selectedPhoto === null ? null : this.photos[this.selectedPhoto]; return !!(p && this.ppbCutBusy === p.dataUrl); },
      async ppbCutOut() {
        const i = this.selectedPhoto, p = i === null ? null : this.photos[i];
        if (!p || !p.dataUrl || this.ppbCutBusy) return;
        this.ppbCutSaid = '';
        if (p.cutUrl) {
          const old = p.cutUrl;
          this._patchPhoto(i, { cutUrl: null });
          setTimeout(function () { try { URL.revokeObjectURL(old); } catch (e) { /* gone */ } }, 1000);
          track('pp_photo_cutout', { on: false });
          return;
        }
        const src = p.dataUrl, t0 = Date.now();
        this.ppbCutBusy = src;
        let blob = null;
        try { blob = await window.PPVision.person(src); } finally { this.ppbCutBusy = null; }
        const at = this.photos.findIndex(function (q) { return q && q.dataUrl === src; });
        if (at < 0) return;                     // replaced meanwhile
        if (!blob) {
          this.ppbCutSaid = 'We couldn’t find a person to cut out in this picture.';
          track('pp_photo_cutout', { on: true, found: false, ms: Date.now() - t0 });
          return;
        }
        this._patchPhoto(at, { cutUrl: URL.createObjectURL(blob) });
        track('pp_photo_cutout', { on: true, found: true, ms: Date.now() - t0 });
      },
      ppbReplaceFrom(files) {
        const i = this._ppbReplaceAt !== null ? this._ppbReplaceAt : this.selectedPhoto;
        this._ppbReplaceAt = null;
        if (i === null || i === undefined || !files || !files.length) return;
        this.replaceOne(files, i);
      },

      // The six looks, each with today's filter for its swatch.
      ppbLooks() {
        const f = {};
        this.effectOptions().forEach(function (o) { f[o.key] = o.filter; });
        return LOOKS.map(function (l) { return { key: l[0], label: l[1], filter: f[l[0]] || 'none' }; });
      },
      ppbLookFilter(k) {
        const o = this.effectOptions().filter(function (x) { return x.key === k; })[0];
        return o ? o.filter : 'none';
      },
      /* A look is today's setEffect; Undo is "back one look" (plan §4), not today's "back to the original".
         The looks a picture had live ON the picture (`looks`, newest last, at most LOOKS_KEPT), so they follow it
         through a swap, start again on a new photo, and the draft keeps them (configurator.js draftFrom and
         loadState carry the field; still pp_draft_v3, an older draft simply has none). */
      ppbSetLook(k) {
        const i = this.selectedPhoto;
        if (i === null || !this.photos[i]) return;
        const now = this.photoEffect(i);
        if (now === k) return;
        this._patchPhoto(i, { looks: (this.photos[i].looks || []).concat([now]).slice(-LOOKS_KEPT) });
        this.setEffect(k);
      },
      ppbCanUndo() {
        const p = this.selectedPhoto === null ? null : this.photos[this.selectedPhoto];
        return !!(p && p.looks && p.looks.length);
      },
      ppbUndoLook() {
        const i = this.selectedPhoto, p = i === null ? null : this.photos[i];
        if (!p || !p.looks || !p.looks.length) return;
        const back = p.looks[p.looks.length - 1];
        this._patchPhoto(i, { looks: p.looks.slice(0, -1) });
        this.setEffect(back);
      },
      // B1's zoom slider: the same setCropScale, with the faded rest of the photo showing while it moves. The slider
      // says its own value (aria-valuetext), so nothing else is announced.
      ppfSlide(v) {
        const i = this.selectedPhoto;
        if (i === null || !this.photos[i]) return;
        this._frameStart(i);
        this.setCropScale(v);
        this._frameEnd(i, null);
      },
      // Reset: zoom, position and tilt (today's Reset was the tilt only).
      ppbCropReset() {
        const i = this.selectedPhoto;
        if (i === null || !this.photos[i]) return;
        this._patchPhoto(i, { cropScale: 1, panX: 50, panY: 50, tilt: 0 });
        this._trackEdit('crop', i);
      },

      // ── Inside top ─────────────────────────────────────────────────────────────────────────────────────
      /* What step 3 offers this design: 'words', 'photo' or 'keep' (see the header). Never in the artist or
         organization modes, whose cards are made from their own designs (today's hasTopChoice rule). */
      ppbTopKind() {
        const d = this.card && this.card.design;
        if (!d || !window.PPTop || this.artist || this.org) return 'keep';
        if (d.CoverTopPlain) return 'words';
        return d.topChoice === 'photo' ? 'photo' : 'keep';
      },
      ppbTopSeg() {
        if (this.ppbTopKind() === 'photo') return [{ key: 'design', label: 'As designed' }, { key: 'photo', label: 'Photo' }];
        return [{ key: 'style', label: 'Style' }, { key: 'words', label: 'Words' }, { key: 'photo', label: 'Photo' }];
      },
      ppbTopTabOn(key) {
        const kind = this.ppbTopKind();
        if (kind === 'keep') return false;
        if (key === 'words') return this.ppbTopTab === 'words' && !this.ppbPhone;
        if (key === 'photo') return this.ppbTopTab === 'photo';
        if (key === 'design') return kind === 'photo' && this.ppbTopTab !== 'photo';
        return kind === 'words' && (this.ppbTopTab === 'style' || (this.ppbTopTab === 'words' && this.ppbPhone));
      },
      // The step is Inside top, the design is of this kind, and this tab is open.
      ppbKindTab(kind, tab) { return this.ppbStep === 'top' && this.ppbTopKind() === kind && this.ppbTopTabOn(tab); },
      /* A tab of the segmented control. Words on a phone is the keyboard, raised in this same tap: the focus is
         the first thing that happens (iOS raises a keyboard only for a focus inside the gesture). */
      ppbTopTabGo(key) {
        /* ⛔ NO SILENT SWAP OF THE ARTIST'S LETTERING (2026-10-11, #7). On As designed the words are the artist's own,
           drawn into the art ("Happy birthday!" in the duck card's hand), and the first keystroke used to replace
           them with whatever layout the card held -- Classic, a serif -- without a word. Words now starts by
           choosing that layout: the Layout menu opens, says why, and the field comes once one is picked
           (ppbPickTemplate). As designed stays a choice in the same menu. */
        if (key === 'words' && this.topMode() === 'design') { this.ppbWordsAsk(); return; }
        if (key === 'words') {
          if (this.ppbPhone) { this.ppbFocusTop('title'); return; }
          this.ppbTopTab = 'words';
          return;
        }
        this.ppbSheet = null;
        if (key === 'photo') {
          this.ppbTopTab = 'photo';
          if (this.topPhoto && this.topPhoto.dataUrl && this.topMode() !== 'photo') this.setTopMode('photo');
          return;
        }
        this.ppbTopTab = key;
        if (this.topMode() === 'photo') this.setTopMode(key === 'design' ? 'design' : (this._ppbWordsMode || 'text'));
      },
      ppbTemplateLabel() {
        const k = this.card.top && this.card.top.template;
        const t = this.topTemplates().filter(function (x) { return x.key === k; })[0];
        return t ? t.label : '';
      },
      ppbFaceLabel(k) { const f = window.PPTop && window.PPTop.FONTS[k]; return f ? f.label : ''; },
      ppbFaceStyle(k) {
        const f = window.PPTop && window.PPTop.FONTS[k];
        return f ? "font-family:'" + f.family + "';font-weight:" + f.weight + ';font-style:' + f.style : '';
      },
      ppbColourHex(k) { return window.PPTop ? window.PPTop.hexOf(k) : '#1B2433'; },
      ppbColourLabel(k) {
        const c = this.topColours().filter(function (x) { return x.key === k || x.hex === k; })[0];
        return c ? c.label : '';
      },
      ppbSheetOpen(name) {
        this.ppbSheet = this.ppbSheet === name ? null : name;
        this.ppbWordsPick = false;
        if (this.ppbSheet === 'layout') { this.cardTool = 'topstyle'; this.topThumbsSoon(); }
      },
      ppbSheetClose() { this.ppbSheet = null; this.ppbWordsPick = false; },
      ppbSheetTitle() {
        if (this.ppbSheet === 'layout' && this.ppbWordsPick) return 'Pick a layout for your words';
        return { layout: 'Layout', face: 'Lettering', colour: 'Colour' }[this.ppbSheet] || '';
      },
      /* Your words on a card that is As designed: the Layout menu, opened to choose the layout they go in (#7). Any
         keyboard goes first, so the menu is not under it (a focus would also close it: _ppbFocusIn). */
      ppbWordsAsk() {
        blurActive();
        if (this.ppbKb) this._ppbKbClose();
        if (!this.ppbPhone) this.ppbTopTab = 'words';
        this.cardTool = 'topstyle';
        this.ppbSheet = 'layout';
        this.ppbWordsPick = true;
        this.topThumbsSoon();
      },
      /* A layout from the menu. When it was opened for the words, the menu has done its job: it closes and the words
         are there to type, in the field (desktop) or on the card with the keyboard (phone: the focus is inside this
         same tap, which is what lets iOS raise the keyboard). */
      ppbPickTemplate(k) {
        const forWords = this.ppbWordsPick;
        this.setTopTemplate(k);
        if (!forWords) return;
        this.ppbSheet = null;
        this.ppbWordsPick = false;
        if (this.ppbPhone) { this.ppbFocusTop('title'); return; }
        this.ppbTopTab = 'words';
        /* Once the field is shown. Firefox and WebKit had not laid it out by the next tick (the focus fell to the
           body, measured 2026-10-11), so it is tried again a beat later if it did not take. */
        const focusTitle = function () {
          const el = document.querySelector('[data-ppb-topfield="title"]');
          if (!el || document.activeElement === el) return;
          try { el.focus(); const n = el.value.length; el.setSelectionRange(n, n); } catch (e) {}
        };
        this.$nextTick(focusTitle);
        setTimeout(focusTitle, 80);
      },
      /* "As designed" draws the artist's greeting only for the occasion it was written for (designTopUrl); on any other
         occasion it is the text-free top, an empty panel. There it is called Blank and shows blank (Daniel, 19 Oct:
         "When I tap 'as designed' on the layout, it removes the text ... remove 'as designed' or turn it into blank"). */
      ppbDesignBlank() {
        const d = this.card && this.card.design;
        if (!d || !d.CoverTopPlain) return false;
        const g = d.topGreets || [], occ = this.topWordsOcc();
        return !(occ && g.indexOf(occ) !== -1);
      },
      ppbDesignLabel() { return this.ppbDesignBlank() ? 'Blank' : 'As designed'; },
      ppbPickDesign() { this.setTopMode('design'); if (this.ppbWordsPick) { this.ppbSheet = null; this.ppbWordsPick = false; } },
      /* The inside top's fields, in B. On As designed a keystroke is not taken: the words there are the artist's
         lettering, and typing must not swap it for a layout nobody chose (#7). The Layout menu asks instead. */
      ppbTopInput(field, e) {
        if (this.topMode() === 'design' && this.ppbTopKind() === 'words') {
          if (e && e.target) e.target.value = (this.card.top && this.card.top[field]) || '';
          this.ppbWordsAsk();
          return;
        }
        this.topInput(field, e);
      },

      /* A line's field on the plane: over the band its words are drawn in, in the template's face at the fitted
         size, so the caret lands near the end of the drawn words. Its own text is transparent (builder-b.css): the
         drawing on the card is what prints. Recomputed as the words, the template and the fit change. */
      ppbTopFieldStyle(field) {
        void this.topPreviewUrl;
        const P = window.PPTop;
        if (!P || !P.measure || !this.card.top) return 'display:none';
        let m = null;
        try { m = P.measure(this.topSpec({ mode: 'text' }), this.topOpts()); } catch (e) { m = null; }
        const S = P.SAFE || 8, b = this.topBox();
        let part = m && (field === 'title' ? m.title : m.sub);
        // An empty line still needs somewhere to put the caret: the band's top for the title, under it for the line.
        if (!part) {
          const h = field === 'title' ? 9 : 4.5;
          const y0 = field === 'title' ? b[0] + (b[1] - b[0]) * 0.3 : Math.min(b[1] - h, (m && m.title ? m.title.y1 : b[0] + 12) + 1.5);
          part = { y0: y0, y1: y0 + h, size: h * 0.8 };
        }
        const face = P.FONTS[field === 'title' ? (m && m.titleFont) : (m && m.subFont)] || P.FONTS['dm-serif'];
        const upper = m && (field === 'title' ? m.titleUpper : m.subUpper);
        const track = m ? (field === 'title' ? m.titleTrack : m.subTrack) : 0;
        const size = part.size / 92 * 100;                    // mm -> cqw of the plane (92 mm wide)
        return 'left:' + (S / 92 * 100).toFixed(3) + '%;right:' + (S / 92 * 100).toFixed(3) + '%;' +
               'top:' + (part.y0 / 120 * 100).toFixed(3) + '%;height:' + ((part.y1 - part.y0) / 120 * 100).toFixed(3) + '%;' +
               "font-family:'" + face.family + "';font-weight:" + face.weight + ';font-style:' + face.style + ';' +
               'font-size:' + size.toFixed(3) + 'cqw;letter-spacing:' + track + 'em;' +
               'text-transform:' + (upper ? 'uppercase' : 'none') + ';caret-color:' + ((m && m.titleColor) || '#1B2433');
      },
      // ⛔ Synchronous: the focus is the first thing, so iOS raises the keyboard for this tap.
      ppbFocusTop(field) {
        const el = document.querySelector('[data-ppb-top="' + (field === 'subtitle' && !this.topOneLine() ? 'subtitle' : 'title') + '"]');
        if (!el) return;
        try {
          el.focus();
          const n = el.value.length;
          el.setSelectionRange(n, n);
        } catch (e) { /* selection APIs are best-effort */ }
      },
      ppbNoteFont(k) {
        this.setCardFont(k, 'note');
        const el = document.querySelector('[data-pp-note-field]');
        if (el && document.activeElement !== el) { try { el.focus(); } catch (e) {} }
      },
      // The keyboard bar's Done: the keyboard goes, and with it the keyboard state, back to the step.
      ppbKbDone() {
        clearTimeout(this._ppbKbT);
        blurActive();
        this._ppbKbClose();
        if (this.ppbStep === 'top' && this.ppbTopKind() === 'words' && this.topMode() !== 'photo') this.ppbTopTab = 'style';
      },

      /* One line on the stage at a time: the inside top's own message first (words that do not fit, a photo that
         could not be read), then, while typing over "As designed", what the first keystroke will do (plan §3,
         "fixed in all three" 7: say it first). */
      ppbNotice() {
        if (this.stage !== 'builder') return null;
        if (this.topMsg && (this.ppbStep === 'top')) return { text: this.topMsg, warn: true };
        /* The refusal stays in view while the line being typed is full (the design's s05), not only for today's
           3.2 s flash: configurator.js topInput keeps, in _topFull, the length each line was refused at. */
        const k = this.ppbTopFocus;
        if (k && this.ppbStep === 'top') {
          const f = this._topFull || {}, v = String((this.card.top && this.card.top[k]) || '');
          if (f[k] != null && v.length >= f[k]) return { text: 'That’s as much as fits here.', warn: true };
        }
        if (k && this.ppbStep === 'top' && this.topMode() === 'design') {
          return { text: 'As designed keeps the artist’s own lettering. Pick a layout for your words.', warn: false };
        }
        /* A soft photo, said on the stage while it is the picture in hand, never blocking (the design's s03; plan §3,
           step 2). Today's own rule decides what is soft (photoIsSoft / photoQuality). */
        const sel = this.selectedPhoto;
        // Photo framing B1 says it under the Zoom row instead, as drawn, and keeps the stage clear over the photo.
        if (this.ppbStep === 'pictures' && sel != null && this.photoIsSoft && this.photoIsSoft(sel) && !this.frameB1()) {
          const q = this.photoQuality && this.photoQuality(sel);
          return { text: q && q.band === 'low' ? 'This photo is a little small for print. Zoom out a touch.'
                                                : 'This photo will print slightly soft. Zoom out to sharpen it.', warn: true };
        }
        return null;
      },

      /* ── The occasion, changed in place (phase 5; Daniel, 9 Oct: changing the design is dropped, the occasion
         stays). The occasions this design is on, made-for first (the catalogue's `occasions`, design-picker.js
         designFit). Offered only where it changes something the customer sees: the inside top's words follow the
         occasion (configurator.js topOnDesign) until they have touched it. The design, the photos and the note stay. */
      ppbOccasions() {
        /* The card keeps a trimmed copy of its design; the catalogue entry carries `occasions` / `made` / `suits`. */
        const cd = this.card && this.card.design;
        const d = cd && ((this.designs || []).find(function (x) { return x.id === cd.id; }) || cd);
        const all = window.PP_OCCASIONS || [], fit = (window.PP && window.PP.designFit) || function () { return 1; };
        const list = (d && Array.isArray(d.occasions)) ? d.occasions : [];
        return list.map(function (slug, i) {
          const o = all.find(function (x) { return x.slug === slug; });
          return o ? { slug: slug, label: o.label, fit: fit(d, slug), i: i } : null;
        }).filter(Boolean).map(function (o) { o.now = o.slug === (cd && this.card.occasion) ? 1 : 0; return o; }, this)
          .sort(function (a, b) { return (b.now - a.now) || (b.fit - a.fit) || (a.i - b.i); });
      },
      ppbOccMenuOn() { return this.stage === 'builder' && this.hasTopChoice() && this.ppbOccasions().length > 1; },
      /* Open or close the menu from the control that was pressed. On desktop it hangs under the header's occasion,
         as drawn, whichever opened it; on a phone it is a sheet from the foot (builder-b.css). */
      ppbOccToggle() {
        this.ppbOccOpen = !this.ppbOccOpen;
        if (!this.ppbOccOpen) return;
        const b = document.querySelector('.ppb-head__occbtn');
        const r = b && b.offsetParent !== null ? b.getBoundingClientRect() : null;
        this._ppbOccAt = r ? { x: Math.round(r.left), y: Math.round(r.bottom + 8) } : null;
      },
      ppbOccBoxStyle() {
        const a = this._ppbOccAt;
        return a && !this.ppbPhone ? 'position:absolute;left:' + a.x + 'px;top:' + a.y + 'px;margin:0' : '';
      },
      ppbOccLine() {
        return (this.card.occasion ? this.occasionLabel() + ' now. ' : '') +
               (this.card.top && this.card.top.touched ? 'Your inside top stays as you made it.' : 'The inside top follows it.');
      },
      ppbSetOccasion(slug) {
        this.ppbOccOpen = false;
        if (!slug || slug === this.card.occasion) return;
        const all = window.PP_OCCASIONS || [];
        const headOf = function (x) { const o = all.find(function (y) { return y.slug === x; }); return (o && o.headline) || ''; };
        const before = this.card.occasion || this.occasion || '';
        this.occasion = slug;
        this.card.occasion = slug;
        // The headline the occasion put there follows it; one they typed stays.
        if (this.card.topSentence && this.card.topSentence === headOf(before)) this.card.topSentence = headOf(slug);
        if (typeof this.topOnDesign === 'function') this.topOnDesign();
      },
      // How long ago the card on the go was started, from the draft's own stamp (the design's s11).
      ppbDraftAge() {
        let ts = 0;
        try { const d = JSON.parse(localStorage.getItem('pp_draft_v3') || 'null'); ts = (d && d.ts) || 0; } catch (e) { ts = 0; }
        if (!ts) return '';
        const day = 864e5, t0 = new Date(); t0.setHours(0, 0, 0, 0);
        const n = Math.floor((t0.getTime() - new Date(ts).setHours(0, 0, 0, 0)) / day);
        return n <= 0 ? 'Started today' : (n === 1 ? 'Started yesterday' : 'Started ' + n + ' days ago');
      },
      ppbDraftBits() {
        const n = this.photos.length, bits = [];
        if (n) bits.push(n + (n === 1 ? ' photo' : ' photos'));
        if ((this.card.bottomNote || '').trim()) bits.push('a note');
        return bits.join(', ');
      },

      // ── Look ───────────────────────────────────────────────────────────────────────────────────────────
      /* Largest first, as drawn. Wide is no longer offered (Daniel and Yonatan, 19 Oct: Super, Classic, Slim, None).
         It stays in BORDER_STOPS and in print: a draft that has it keeps it and prints it, and no tile is ticked. */
      ppbBorders() { return this.borderStops().filter(function (b) { return b.key !== 'wide'; }).slice().reverse(); },
      // The stop's own window, to scale, inside the tile's paper: the 3:4 panel on screen, 56.25 x 75 mm, whose window is
      // the 5:7 table's 3.75 mm shorter (configurator.js SHOW_H_MM, Yonatan's 3:4 strip, 22 Oct).
      ppbStopStyle(stop) {
        const w = this.captionsOn() ? stop.cap : stop.full;
        const r = stop.key === 'none' ? 0 : Math.min(6, this.roundedMm() * 0.9);
        return 'left:' + (stop.side / 56.25 * 100).toFixed(2) + '%;right:' + (stop.side / 56.25 * 100).toFixed(2) + '%;' +
               'top:' + (w.top / 75 * 100).toFixed(2) + '%;height:' + ((w.h - 3.75) / 75 * 100).toFixed(2) + '%;border-radius:' + r.toFixed(1) + 'px';
      },

      // ── The header and Preview ─────────────────────────────────────────────────────────────────────────
      // Open / Closed. Today's fold, so the card closes and opens exactly as it does in today's Preview. Called by Look's
      // views (card-views.js ppbSetView) since 24 Oct, when the header's and the stage's toggles went.
      ppbShowClosed(closed) {
        if (!!this.foldedInEditor === !!closed) return;
        this.toggleFold();
      },

      // ── The keyboard ───────────────────────────────────────────────────────────────────────────────────
      /* Set from the focus, on a phone only: a desktop types with the card in view and the tray where it is.
         Cleared a beat after the focus leaves, and kept if it lands on another of the card's fields in the
         meantime, so moving from the title to the second line does not flash the step's chrome back. */
      _ppbFocusIn(e) {
        const tf = e.target && e.target.getAttribute && (e.target.getAttribute('data-ppb-top') || e.target.getAttribute('data-ppb-topfield'));
        this.ppbTopFocus = tf || null;
        const kind = kbFieldOf(e.target);
        // Typing on the card zoomed out: back to the step's close-up, where the words are large enough to read.
        if (kind && kind !== 'send' && this.ppbZoomPref === 'out') this.ppbZoomPref = null;
        if (e.target && e.target.matches && e.target.matches('[data-ppb-from] [data-ppb-sender="line1"]')) this._ppbSenderAc(e.target);
        if (!kind || this.stage !== 'builder' || !this.ppbPhone) return;
        clearTimeout(this._ppbKbT);
        if (kind === 'top') this.ppbKbField = e.target.getAttribute('data-ppb-top') || 'title';
        this.ppbSheet = null;
        this.ppbKb = kind;
        if (kind === 'send') {
          this._ppbSendEl = e.target;
          this.ppbSendForm = e.target.closest && e.target.closest('[data-ppb-from]') ? 'from'
            : (e.target.closest && e.target.closest('[data-ppb-import]') ? 'import' : 'people');
          this._ppbSendReveal();
        }
      },
      /* The keyboard state ends. On Send the page gets its chrome back above the form as the keys go (the envelope, the
         choice), which would push the field they were in out of view and lose their place: it stays where it was on the
         page instead. */
      _ppbKbClose() {
        const page = this.ppbKb === 'send' && document.querySelector('.ppb-sendpage'), el = this._ppbSendEl;
        const off = page && el && el.isConnected ? el.getBoundingClientRect().top - page.getBoundingClientRect().top : null;
        this.ppbKb = null;
        if (off === null) return;
        /* Idempotent, so it runs a few times: Chromium's scroll anchoring may already have done it, and WebKit (no
           anchoring) measured the page before the chrome was back on the first try (headless WebKit, 9 Oct). */
        const keep = function () {
          if (!el.isConnected) return;
          const d = (el.getBoundingClientRect().top - page.getBoundingClientRect().top) - off;
          if (Math.abs(d) > 1) page.scrollTop += d;
        };
        this.$nextTick(keep);
        setTimeout(keep, 60);
        setTimeout(keep, 200);
      },
      _ppbFocusOut() {
        this.ppbTopFocus = null;
        if (!this.ppbKb) return;
        const self = this;
        clearTimeout(this._ppbKbT);
        this._ppbKbT = setTimeout(function () {
          if (kbFieldOf(document.activeElement)) return;
          self._ppbKbClose();
          if (self.ppbStep === 'top' && self.ppbTopKind() === 'words' && self.topMode() !== 'photo') self.ppbTopTab = 'style';
        }, 180);
      },

      /* ── Send: "Who's it for?" (phase 3, builder-b-send.liquid) ─────────────────────────────────────────────────
         The choice of who it goes to over today's `recipients`. To me and Someone else are one person; Several is up to
         six. Going from Several to one person keeps the first and puts the others aside (_ppbExtra), so nothing typed is
         lost if they change their mind; Several gives them back. Every cart line is still one person, as today. */
      ppbSendOpts() {
        /* A set (assets/pp-set.js; reviewers.com, 18 Oct) names who it goes to: To me only, one card. */
        const set = window.PPSet && window.PPSet.active();
        if (set && set.sendTo === 'me' && !this.artist && !this.org) return [{ key: 'me', phone: 'To me', desk: 'To me' }];
        return [{ key: 'me', phone: 'To me', desk: 'To me' },
                { key: 'other', phone: 'Someone else', desk: 'To someone else' },
                { key: 'several', phone: 'Several people', desk: 'To several people' }];
      },
      /* On a phone, a tap on the envelope or on To me / Someone else / Several people brings the form up: the three
         choices at the top of the page, the first fields under them (Daniel's Pop List, 12 Oct: "the envelope fills the
         screen and nothing hints at the form below"). Smooth, after the form has redrawn for the choice. */
      ppbSendToForm() {
        if (!this.ppbPhone) return;
        const self = this;
        this.$nextTick(function () {
          const page = document.querySelector('.ppb-sendpage'), who = page && page.querySelector('.ppb-who');
          if (!page || !who || self.ppbKb === 'send') return;
          const y = who.getBoundingClientRect().top - page.getBoundingClientRect().top + page.scrollTop - 12;
          page.scrollTo({ top: Math.max(0, y), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        });
      },
      ppbSendGo(k) {
        if (k === this.sendTo) return;
        this.sendTo = k;
        if (k === 'several') {
          const room = this.recipMax() - this.recipients.length;
          if (this._ppbExtra.length && room > 0) this.recipients.push.apply(this.recipients, this._ppbExtra.splice(0, room));
          const open = this.firstIncompleteRecipient();
          this.recipOpen = open;
          return;
        }
        if (this.recipients.length > 1) this._ppbExtra = this.recipients.splice(1).concat(this._ppbExtra);
        if (!this.recipients.length) this.recipients.push(this.blankRecipient());
        this.recipOpen = 0;
        /* To me: their own name and address, from From you when they typed it there and the form is still empty
           (the design's To me screen shows it filled). Never over anything typed. */
        const r = this.recipients[0], s = this.sender || {};
        const empty = !['firstName', 'lastName', 'line1', 'line2', 'city', 'state', 'zip'].some(function (k) { return String(r[k] || '').trim(); });
        if (k === 'me' && empty && this.ppbSenderComplete() && !s.abroad) {
          Object.assign(r, { firstName: s.firstName, lastName: s.lastName, line1: s.line1, line2: s.line2,
                             city: s.city, state: s.state, zip: s.zip, country: 'US' });
        }
      },
      ppbPeopleTitle() {
        if (this.sendTo === 'me') return 'Your name and address';
        if (this.sendTo === 'several' && this.ppbKb === 'send' && this.ppbSendForm === 'import') return this.ppbImp ? 'Fixing your list' : 'Add a list';
        if (this.sendTo === 'several' && this.ppbKb !== 'send') return 'The people · ' + this.recipients.length;
        return 'Who it goes to';
      },
      ppbSendEdit(i) { this.recipOpen = i; },
      ppbSendRemove(i) {
        if (this.recipients.length < 2) return;
        this.removeRecipient(i);
        if (this.recipOpen < 0) this.recipOpen = this.firstIncompleteRecipient();
      },
      ppbSendRefused() {
        const r = this.recipOpen >= 0 ? this.recipients[this.recipOpen] : null;
        return !!(r && this.recipBad(r).length);
      },
      // The person on the envelope: the one open, else the first.
      _ppbShown() {
        const i = this.recipOpen >= 0 ? this.recipOpen : 0;
        return this.recipients[i] || this.blankRecipient();
      },
      /* One person as the envelope prints them: the same lines as recipToLine (configurator.js), which is what
         `Delivery To` carries and print sets, so what the drawn envelope shows is what the paper says. */
      _ppbAddr(r) {
        const t = function (x) { return String(x || '').trim(); };
        const p = this.recipParts(r), us = p.us;
        const name = [t(r.firstName), t(r.lastName)].filter(Boolean).join(' ');
        const street = [p.line1, p.line2].filter(Boolean).join(' ');
        const town = [[p.city, p.state].filter(Boolean).join(', '), p.zip].filter(Boolean).join(' ');
        const country = us ? '' : (window.PPAddress ? window.PPAddress.countryLine(t(r.country).toUpperCase()) : t(r.country));
        return { name: name, street: street, town: town, country: country, us: us };
      },
      // The envelope's lines, with the empty ones as their placeholders (drawn lighter).
      /* ── Org mode's Send page (14 Oct) ─────────────────────────────────────────────────────────────────────────────
         One card of a business's order: 'default' is everyone's card, else one person's own (business-worker cards.js).
         The business app answers with the card's label only ("Everyone’s card", "Dana’s card"), not the people. */
      ppbOrgEveryone() {
        try { return new URLSearchParams(location.search).get('card') === 'default'; } catch (e) { return false; }
      },
      ppbOrgName() {
        const l = String((this.org && this.org.label) || '');
        const m = /^(.+)’s card$/.exec(l);
        return m && m[1] !== 'Their' ? m[1] : '';
      },
      ppbOrgLines() {
        if (this.ppbOrgEveryone()) {
          return ['Everyone on your order’s list gets this card, each in their own envelope, at the address the list gives.',
                  'Someone with a card of their own keeps it.'];
        }
        const who = this.ppbOrgName();
        return [(who ? who + ' gets' : 'This person gets') + ' this card, at the address on your order’s list.'];
      },
      ppbOrgOrderLine() {
        const order = (this.org && this.org.orderName) || '';
        return 'It saves to ' + (order ? '“' + order + '”' : 'your order') +
               '. Who gets a card, and where it goes, is changed on the order’s list.';
      },
      ppbEnvTo() {
        if (this.org) {
          const who = this.ppbOrgEveryone() ? 'Everyone on your list' : (this.ppbOrgName() || 'The person on your list');
          return [{ t: who, ph: false }, { t: 'Their address', ph: true }, { t: 'from your order’s list', ph: true }];
        }
        const a = this._ppbAddr(this._ppbShown()), me = this.sendTo === 'me';
        const out = [{ t: a.name || (me ? 'Your name' : 'Their name'), ph: !a.name },
                     { t: a.street || 'Street address', ph: !a.street },
                     { t: a.town || (a.us ? 'City, state, ZIP' : 'City, postcode'), ph: !a.town }];
        if (a.country) out.push({ t: a.country, ph: false });
        return out;
      },

      /* ── Many people (Daniel, 9 Oct: "remove limits") ────────────────────────────────────────────────────────────
         The list shown: everyone up to PEOPLE_FOLD; past it the first PEOPLE_FOLD (the open person always among them),
         or what the search finds, or only the ones still to finish, or everyone once Show all is pressed. Each item
         is { r, i }: `i` is the person's place in recipients[], which every one of today's calls takes. */
      ppbPeopleBig() { return this.sendTo === 'several' && this.recipients.length > PEOPLE_FOLD; },
      ppbPeopleView() {
        const self = this, all = this.recipients.map(function (r, i) { return { r: r, i: i }; });
        if (!this.ppbPeopleBig() || this.ppbKb === 'send') return all;
        const q = String(this.ppbPeopleQ || '').trim().toLowerCase();
        let list = all;
        if (q) list = list.filter(function (x) { return (self.recipLine(x.r) + ' ' + (x.i + 1)).toLowerCase().indexOf(q) !== -1; });
        if (this.ppbPeopleTodo) list = list.filter(function (x) { return !self.recipComplete(x.r); });
        if (!this.ppbPeopleAll && !q && !this.ppbPeopleTodo && list.length > PEOPLE_FOLD) {
          const open = this.recipOpen;
          list = list.slice(0, PEOPLE_FOLD).concat(open >= PEOPLE_FOLD ? [all[open]] : []);
        }
        return list;
      },
      ppbPeopleTodoN() {
        const self = this;
        return this.recipients.reduce(function (n, r) { return n + (self.recipComplete(r) ? 0 : 1); }, 0);
      },
      ppbPeopleMore() {
        if (!this.ppbPeopleBig() || this.ppbPeopleAll || this.ppbPeopleTodo || String(this.ppbPeopleQ || '').trim()) return '';
        return 'Show all ' + this.recipients.length;
      },
      // Remove every person and start the list again (a long import that was the wrong file).
      ppbPeopleClear() {
        const n = this._ppbRealPeople();
        if (n > 2 && !window.confirm('Take all ' + n + ' people off the list and start again?')) return;
        this.recipients.splice(0, this.recipients.length, this.blankRecipient());
        this.recipOpen = 0;
        this.ppbPeopleQ = ''; this.ppbPeopleAll = false; this.ppbPeopleTodo = false;
      },

      /* ── A list, pasted or from a file (Daniel, 9 Oct) ────────────────────────────────────────────────────────────
         "pasting a list or uploading a csv … provide a template or automatically read a csv provided it has the right
         info per name … allow uploading even an imperfect csv and just show people where they need to fix."
         assets/pp-recipient-import.js reads the text (nothing is uploaded: a file is read here, in the browser). Every person is
         then judged by today's own checks, recipMissing and recipBad (PPAddress.problems: the ZIP and state agree, the
         countries we post to, the design's countries), so an imported person passes exactly when a typed one would.
         The list is looked over before anyone joins the people: what is ready can be added at once; a row to fix is
         fixed in place, field by field, or left out; the same person twice is said. Each person added is today's
         recipient, so the cart lines are today's: one per person, Delivery To and _Ship (+ _From when From you is
         filled). No new order property. */
      ppbImpStart() {
        this.ppbImpOpen = true; this.ppbImpErr = '';
        if (window.PPAddress) window.PPAddress.loadZips();
        const self = this;
        this.$nextTick(function () {
          // The panel opens below the people: bring it into the Send page's view (its own scrollTop, never scrollIntoView).
          const page = document.querySelector('.ppb-sendpage'), box = document.querySelector('[data-ppb-import]');
          if (page && box) page.scrollTop += box.getBoundingClientRect().top - page.getBoundingClientRect().top - 12;
          const t = document.querySelector('[data-ppb-import] textarea');
          if (t && !self.ppbPhone) { try { t.focus(); } catch (e) { /* best-effort */ } }
        });
      },
      ppbImpCancel() { this.ppbImpOpen = false; this.ppbImp = null; this.ppbImpErr = ''; this.ppbImpText = ''; },
      ppbImpTemplate() {
        if (window.PPImport) window.PPImport.downloadTemplate();
        track('pp_import_template_downloaded', { where: 'builder' });
      },
      ppbImpPaste() {
        if (!String(this.ppbImpText || '').trim()) { this.ppbImpErr = 'Paste your list in the box first, one person on each line.'; return; }
        this.ppbImpLoad(this.ppbImpText, 'paste', '');
      },
      ppbImpFile(e) {
        const f = e && e.target && e.target.files && e.target.files[0];
        const self = this;
        if (!f || !window.PPImport) return;
        this.ppbImpErr = ''; this.ppbImpBusy = true;
        window.PPImport.readFile(f).then(function (text) {
          self.ppbImpLoad(text, 'file', f.name);
        }, function (err) {
          self.ppbImpBusy = false;
          self.ppbImpErr = err && err.message === 'binary'
            ? 'That looks like a spreadsheet file. Save it as CSV first (File, then Save As or Download, then CSV), or copy the rows and paste them here.'
            : (err && err.message === 'large' ? 'That file is too big to be a list of addresses. Is it the right one?'
                                              : 'We couldn’t open that file. Try again, or paste the rows instead.');
          track('pp_recipients_import_failed', { where: 'builder', reason: (err && err.message) || 'read' });
        });
        try { e.target.value = ''; } catch (x) { /* the same file can be picked again */ }
      },
      async ppbImpLoad(text, src, name) {
        this.ppbImpBusy = true; this.ppbImpErr = '';
        // The ZIP table first: it is what puts back a ZIP's leading zero, and what checks every ZIP.
        try { if (window.PPAddress) { await window.PPAddress.loadZips(); this._zipTick++; } } catch (e) { /* checked by format alone */ }
        let res;
        try { res = window.PPImport.parse(text); } catch (e) { res = { rows: [] }; }
        this.ppbImpBusy = false;
        if (!res.rows.length) {
          this.ppbImpErr = 'We couldn’t find any names and addresses in that. Put one person on each line, or use the template.';
          track('pp_recipients_import_failed', { where: 'builder', reason: 'empty', source: src });
          return;
        }
        const self = this;
        const rows = res.rows.map(function (x) {
          return { id: ++impSeq, line: x.line, p: Object.assign(self.blankRecipient(), x.person), countryRaw: x.person.countryRaw || '',
                   notes: x.notes, keep: false, skip: false, all: false, flagged: false, fields: [] };
        });
        this.ppbImp = { rows: rows, src: src, name: name || '', total: rows.length, header: res.header };
        // Flagged once, now: a row that needed a fix stays in the list to fix, ticked, when it is fixed, so nothing
        // jumps away from under the thumb that is fixing it.
        rows.forEach(function (r) {
          r.fields = self.ppbImpProblems(r).map(function (x) { return x.key; });
          r.flagged = !!(r.fields.length || self.ppbImpDup(r));
        });
        this.ppbImpAll = false; this.ppbImpReadyOpen = false;
        if (this.sendTo !== 'several') this.ppbSendGo('several');
        const sum = this.ppbImpSum();
        track('pp_recipients_imported', { where: 'builder', source: src, rows: rows.length, ready: sum.ready, fix: sum.fix,
                                          dupes: sum.dup, header: !!res.header });
      },
      /* What is wrong with one row, as { key, msg }, wrong values first, the way the form says them. A country the
         reader could not tell is asked for alone: until it is chosen, nothing else can be judged. */
      ppbImpProblems(row) {
        const p = row.p;
        if (!String(p.country || '').trim()) {
          return [{ key: 'country', msg: row.countryRaw ? 'Which country is “' + row.countryRaw + '”? Choose it from the list.' : 'Choose the country.' }];
        }
        const bad = this.recipBad(p).map(function (b) { return { key: b.key, msg: b.msg }; });
        const miss = this.recipMissing(p).filter(function (m) { return !bad.some(function (b) { return b.key === m.key; }); })
          .map(function (m) { return { key: m.key, msg: (m.key === 'zip' && String(p.zip || '').trim() ? 'Check the ZIP code: it needs five digits' : 'Add ' + m.label) + '.' }; });
        return bad.concat(miss);
      },
      // The same person as a row above it, or as someone already on the list: their words, or ''.
      ppbImpDup(row) {
        if (row.keep || !window.PPImport) return '';
        const k = window.PPImport.dupKey(row.p), rows = this.ppbImp ? this.ppbImp.rows : [];
        for (let j = 0; j < rows.length && rows[j].id !== row.id; j++) {
          if (!rows[j].skip && window.PPImport.dupKey(rows[j].p) === k) return 'Same as row ' + rows[j].line + '.';
        }
        if (this.recipients.some(function (r) { return window.PPImport.dupKey(r) === k; })) return 'Already on your list.';
        return '';
      },
      ppbImpReady(row) { return !row.skip && !this.ppbImpProblems(row).length && !this.ppbImpDup(row); },
      ppbImpSum() {
        const self = this, rows = this.ppbImp ? this.ppbImp.rows.filter(function (r) { return !r.skip; }) : [];
        let ready = 0, fix = 0, dup = 0;
        rows.forEach(function (r) {
          if (self.ppbImpProblems(r).length) fix++;
          else if (self.ppbImpDup(r)) dup++;
          else ready++;
        });
        const room = Math.max(0, this.recipMax() - this._ppbRealPeople());
        return { ready: ready, fix: fix, dup: dup, room: room, adds: Math.min(ready, room) };
      },
      // People already on the list who are more than the blank first form Several starts with.
      _ppbRealPeople() {
        const self = this;
        return this.recipients.filter(function (r) { return !self._ppbBlank(r); }).length;
      },
      _ppbBlank(r) { return !['firstName', 'lastName', 'line1', 'line2', 'city', 'state', 'zip'].some(function (k) { return String(r[k] || '').trim(); }); },
      ppbImpHead() {
        const s = this.ppbImpSum(), parts = [];
        if (s.ready) parts.push(s.ready + ' ready');
        if (s.fix) parts.push(s.fix + (s.fix === 1 ? ' needs a fix' : ' need a fix'));
        if (s.dup) parts.push(s.dup + (s.dup === 1 ? ' looks like a duplicate' : ' look like duplicates'));
        return parts.join(', ') || 'Nothing left to add';
      },
      ppbImpCta() {
        const s = this.ppbImpSum();
        if (!s.adds) return '';
        if (s.adds < s.ready) return 'Add the first ' + s.adds;
        if (!s.fix && !s.dup) return s.adds === 1 ? 'Add them' : 'Add all ' + s.adds;
        return s.adds === 1 ? 'Add the one that’s ready' : 'Add the ' + s.adds + ' that are ready';
      },
      ppbImpRoomLine() {
        const s = this.ppbImpSum();
        if (s.ready <= s.room) return '';
        return 'One order can take ' + this.recipMax() + ' cards for now, so ' + (s.room ? 'the first ' + s.room + ' go in this one' : 'this one is full') +
               '. Check out, then start another order for the rest.';
      },
      // The rows to fix (and the duplicates), flagged at import, in the file's order: IMPORT_SHOWN, or all.
      ppbImpFixRows() {
        if (!this.ppbImp) return [];
        const rows = this.ppbImp.rows.filter(function (r) { return r.flagged && !r.skip; });
        return this.ppbImpAll ? rows : rows.slice(0, IMPORT_SHOWN);
      },
      ppbImpFixMore() {
        if (!this.ppbImp || this.ppbImpAll) return 0;
        return Math.max(0, this.ppbImp.rows.filter(function (r) { return r.flagged && !r.skip; }).length - IMPORT_SHOWN);
      },
      ppbImpReadyRows() {
        const self = this;
        return this.ppbImp ? this.ppbImp.rows.filter(function (r) { return !r.flagged && self.ppbImpReady(r); }) : [];
      },
      /* The fields a row shows, in the form's order: the ones it was flagged for, and any with a problem now (a ZIP
         typed right can turn up a state that disagrees); every field once "Edit all" is pressed. A field fixed stays
         on screen: nothing vanishes from under the thumb that fixed it. */
      ppbImpRowFields(row) {
        const ALL = ['firstName', 'lastName', 'country', 'line1', 'line2', 'city', 'state', 'zip'];
        if (row.all) return ALL;
        const keys = (row.fields || []).concat(this.ppbImpProblems(row).map(function (x) { return x.key; }));
        return ALL.filter(function (k) { return keys.indexOf(k) !== -1; });
      },
      ppbImpLabel(row, k) {
        const us = this.recipIsUS(row.p);
        return ({ firstName: 'First name', lastName: 'Last name', country: 'Country', line1: 'Street address',
                  line2: 'Apt / suite (optional)', city: 'City', state: us ? 'State' : 'Region (optional)',
                  zip: us ? 'ZIP' : 'Postcode' })[k];
      },
      ppbImpFieldBad(row, k) { return this.ppbImpProblems(row).some(function (x) { return x.key === k; }); },
      ppbImpLine(row) {
        const l = this.recipLine(row.p);
        return l || 'Row ' + row.line + ', no name or address';
      },
      ppbImpSkip(row) { row.skip = true; },
      ppbImpKeep(row) { row.keep = true; },
      ppbImpAdd() {
        if (!this.ppbImp) return;
        const self = this, s = this.ppbImpSum();
        let n = s.adds;
        if (!n) return;
        const take = this.ppbImp.rows.filter(function (r) { return self.ppbImpReady(r); }).slice(0, n);
        // The blank first form Several opens with is replaced, never left above the list as "not finished".
        const keep = this.recipients.filter(function (r) { return !self._ppbBlank(r); });
        const add = take.map(function (r) {
          const p = r.p;
          return { firstName: p.firstName, lastName: p.lastName, line1: p.line1, line2: p.line2, city: p.city,
                   state: p.state, zip: p.zip, country: String(p.country || 'US').toUpperCase() };
        });
        this.recipients.splice.apply(this.recipients, [0, this.recipients.length].concat(keep.concat(add)));
        const ids = take.map(function (r) { return r.id; });
        this.ppbImp.rows = this.ppbImp.rows.filter(function (r) { return ids.indexOf(r.id) === -1; });
        this.recipOpen = -1;
        this.ppbPeopleQ = ''; this.ppbPeopleTodo = false;
        const left = this.ppbImp.rows.filter(function (r) { return !r.skip; }).length;
        track('pp_recipients_import_added', { where: 'builder', added: add.length, left: left, people: this.recipients.length });
        if (!left) { this.ppbImp = null; this.ppbImpOpen = false; this.ppbImpText = ''; }
      },
      // Done with the list: what is still to fix is left out (nothing of it was added).
      ppbImpDone() { this.ppbImp = null; this.ppbImpOpen = false; this.ppbImpText = ''; },

      /* ── From you: the sender on the envelope (phase 4, approved by Daniel 8 Oct) ───────────────────────────────
         To someone else or several people, the envelope's top left is the sender's own name and address, so the
         person sees who it is from (decisions.md 2026-10-05). Written to the order as `_From`, the block exactly as it
         prints, and `_From Ship`, the same as JSON, on every line (contract: "The sender on the envelope").
           · Optional, never half: an empty From you prints Picturepops; a started one is finished or cleared before
             Add to cart (plan §7, question 4).
           · A return address has to be in the US (Yonatan, efee85c: shipper_frame.sender_rule). A sender outside the
             US gives a name only, and print sets it over our US address. Never a 4th line.
           · To me: no sender; the envelope says Picturepops, as today. */
      /* ⛔ Not on an artist's free card (14 Oct): its lines go to the artist platform exactly as classic built them,
         and classic had no From you, so no `_From` on a free-card order until that is decided with print. */
      /* Nor on a business's card (org mode): its envelopes come from the order's list, and the business app drops `_From`. */
      ppbFromShown() { return !this.artist && !this.org && (this.sendTo === 'other' || this.sendTo === 'several' || !this.sendTo); },
      _ppbSender() { return this.sender || {}; },
      ppbSenderStarted() {
        const s = this._ppbSender();
        return ['firstName', 'lastName', 'line1', 'line2', 'city', 'state', 'zip']
          .some(function (k) { return String(s[k] || '').trim(); });
      },
      ppbSenderMissing() {
        const s = this._ppbSender(), t = function (k) { return String(s[k] || '').trim(); };
        const need = [{ key: 'firstName', label: 'your first name' }, { key: 'lastName', label: 'your last name' }];
        if (!s.abroad) need.push({ key: 'line1', label: 'your street address' }, { key: 'city', label: 'your city' },
                                 { key: 'state', label: 'your state' }, { key: 'zip', label: 'your ZIP code' });
        return need.filter(function (f) { return f.key === 'zip' ? !/^[0-9]{5}$/.test(t('zip')) : !t(f.key); });
      },
      // A wrong value (a state that isn't one, a ZIP of another state) is said at once, as for the people.
      ppbSenderBad() {
        void this._zipTick;
        const s = this._ppbSender();
        if (s.abroad || !window.PPAddress) return [];
        return window.PPAddress.problems({ country: 'US', state: s.state, zip: s.zip });
      },
      ppbSenderComplete() { return this.ppbSenderStarted() && !this.ppbSenderMissing().length && !this.ppbSenderBad().length; },
      ppbSenderHalf() { return this.ppbFromShown() && this.ppbSenderStarted() && !this.ppbSenderComplete(); },
      ppbSenderFieldMissing(key) {
        if (!this.ppbFromShown()) return false;
        if (this.ppbSenderBad().some(function (b) { return b.key === key; })) return true;
        return !!(this.recipTried && this.ppbSenderStarted() &&
                  this.ppbSenderMissing().some(function (m) { return m.key === key; }));
      },
      // The block as it prints: three lines at home, the name alone abroad (print adds our address under it).
      ppbSenderLines() {
        const s = this._ppbSender(), t = function (x) { return String(x || '').trim(); };
        const name = [t(s.firstName), t(s.lastName)].filter(Boolean).join(' ');
        if (s.abroad) return [name];
        const p = this.recipParts(s);   // without a repeated unit or state (configurator.js recipParts)
        return [name, [p.line1, p.line2].filter(Boolean).join(', '),
                [p.city, p.state].filter(Boolean).join(', ') + (p.zip ? ' ' + p.zip : '')];
      },
      // `_From`, or '' when the envelope carries ours.
      ppbFromValue() {
        if (!this.ppbFromShown() || !this.ppbSenderComplete()) return '';
        return this.ppbSenderLines().join(' · ');
      },
      ppbSenderAbroad(on) { this.sender.abroad = !!on; },
      /* Address suggestions on From you's street, as each person's (configurator.js, the recipOpen watcher): US only,
         the pick written to the sender state and x-model paints the fields, never the DOM. The apartment is only
         overwritten when Google returns one. Attached once, on the field's first focus; a sender abroad has no street. */
      _ppbSenderAc(el) {
        if (this._ppbSenderAcOn || !window.PPAddress || !el) return;
        this._ppbSenderAcOn = true;
        const self = this;
        window.PPAddress.attachAutocomplete(el, function (f) {
          const s = self.sender;
          if (!s || s.abroad || (f.country && f.country !== 'US')) return;
          if (f.line1) s.line1 = f.line1;
          if (f.line2) s.line2 = f.line2;
          if (f.city) s.city = f.city;
          if (f.state) s.state = f.state;
          if (f.zip) s.zip = f.zip;
        }, { country: function () { return 'US'; } }).catch(function () { self._ppbSenderAcOn = false; });
      },
      ppbSenderClear() {
        const s = this.sender;
        ['firstName', 'lastName', 'line1', 'line2', 'city', 'state', 'zip'].forEach(function (k) { s[k] = ''; });
        s.abroad = false;
      },
      // What the foot says: a wrong sender value at once, a half sender once they have pressed, then the people's.
      ppbSendHint() {
        if (this.org) return '';                 // nobody to finish: the order's list holds the people
        if (this.ppbFromShown()) {
          const bad = this.ppbSenderBad();
          if (bad.length) return 'Your address: ' + bad[0].msg;
          if (this.recipTried && this.ppbSenderHalf()) {
            const m = this.ppbSenderMissing();
            return (m.length === 1 ? 'Still need ' + m[0].label : 'Your return address isn’t finished') +
                   '. Finish it, or clear it and the envelope carries ours.';
          }
        }
        return this.recipHint();
      },

      /* Top left: the sender's lines (placeholders until typed); ours for To me, or when From you is left empty and
         they have moved on. Our US address is drawn, never sent: print has it (the RETURN_ADDRESS secret). */
      ppbEnvFrom() {
        const OURS = ['2108 N St #14442', 'Sacramento, CA 95816']; // the company mailbox; never 'Ste N' (Northwest's own suite; Yonatan 2026-10-12)
        if (!this.ppbFromShown()) return [{ t: 'Picturepops', ph: false }, { t: 'Return address', ph: true }];
        const s = this._ppbSender(), L = this.ppbSenderLines();
        if (s.abroad) return [{ t: L[0] || 'Your name', ph: !L[0] }, { t: OURS[0], ph: false }, { t: OURS[1], ph: false }];
        return [{ t: L[0] || 'Your name', ph: !L[0] }, { t: L[1] || 'Your street', ph: !L[1] },
                { t: (L[2] || '').trim() || 'City, state, ZIP', ph: !(L[2] || '').trim() }];
      },
      ppbEnvNote() {
        if (this.org) return this.ppbOrgEveryone() ? 'Each envelope is addressed from your order’s list.' : 'The envelope is addressed from your order’s list.';
        if (this.sendTo === 'me') return 'Sent to yourself, the return address is Picturepops.';
        // An artist's free card has no From you (ppbFromShown): the envelope carries ours.
        if (this.artist) return this.sendTo === 'several'
          ? 'Each envelope carries the Picturepops return address. Shown: person ' + ((this.recipOpen >= 0 ? this.recipOpen : 0) + 1) + '.'
          : 'The return address is Picturepops.';
        const abroad = this._ppbSender().abroad;
        if (this.sendTo === 'several') {
          return (abroad ? 'Each envelope carries your name over our US address, so every person sees it’s from you.'
                         : 'Each envelope carries your name and address top left, so every person sees it’s from you.') +
                 ' Shown: person ' + ((this.recipOpen >= 0 ? this.recipOpen : 0) + 1) + '.';
        }
        const who = String((this._ppbShown() || {}).firstName || '').trim();
        return (abroad ? 'Your name goes top left over our US address, so ' : 'Your name and address go top left on the envelope, so ') +
               (who ? who + ' sees' : 'they see') + ' it’s from someone they know.';
      },
      ppbEnvLine() {
        const a = this._ppbAddr(this._ppbShown());
        return [a.name, a.street, a.town, a.country].filter(Boolean).join(', ');
      },
      // The live line above the form while typing: the return address for From you, else the person's.
      ppbKbLine() {
        if (this.ppbSendForm === 'import') {
          const row = this._ppbImpFocusRow();
          if (!this.ppbImp) return 'One person on each line: their name, then their address.';
          return row ? 'On the envelope: ' + (this.recipLine(row.p) || 'not filled in yet') : 'Fixing your list';
        }
        if (this.ppbSendForm === 'from') {
          const L = this.ppbSenderLines().filter(function (x) { return String(x).trim(); });
          return 'Return address: ' + (L.length ? L.join(', ') + (this._ppbSender().abroad ? ', over our US address' : '') : 'not filled in yet');
        }
        return 'On the envelope: ' + (this.ppbEnvLine() || 'not filled in yet');
      },
      ppbKbTitle() { return this.ppbSendForm === 'from' ? 'From you' : this.ppbPeopleTitle(); },
      _ppbImpFocusRow() {
        const a = document.activeElement, li = a && a.closest && a.closest('[data-ppb-improw]');
        const id = li && +li.getAttribute('data-ppb-improw');
        return (this.ppbImp && this.ppbImp.rows.find(function (r) { return r.id === id; })) || null;
      },
      ppbEnvAria() {
        if (this.org) return 'The envelope: to ' + this.ppbEnvTo()[0].t + ', at the address on your order’s list';
        const from = this.ppbFromShown() && this.ppbSenderStarted() ? this.ppbSenderLines().join(', ') : 'Picturepops';
        return 'The envelope: from ' + from + ', to ' + (this.ppbEnvLine() || 'nobody yet');
      },
      /* The keyboard bar's ‹ Previous and Next ›: the fields on show, in order (with the keys up only the form being
         typed in is on show). ⛔ Synchronous, inside the tap, and the chips keep the keyboard up (pointerdown
         prevented), so iOS moves the caret without dropping the keys. */
      ppbSendNav(d) {
        const vis = function (root) {
          return root ? Array.prototype.filter.call(root.querySelectorAll('[data-ppb-send-field]'), function (f) { return f.offsetParent !== null; }) : [];
        };
        const fields = vis(document.querySelector('[data-ppb-send]'));
        const at = fields.indexOf(document.activeElement);
        let next = fields[at + d];
        /* From you and the person are two forms, and with the keys up only the one being typed in shows. Next from
           From you's last field goes on to the person, Previous from the person's first back to From you: the other
           form is shown here, by hand and in the same tap, because Alpine's x-show would only catch up after it, and
           a field still display:none takes no focus (iOS: the keys would drop). */
        if (!next && this.ppbKb === 'send') {
          const toPeople = d > 0 && this.ppbSendForm === 'from';
          const toFrom = d < 0 && this.ppbSendForm === 'people' && this.ppbFromShown();
          const other = toPeople ? document.querySelector('[data-ppb-send] .ppb-people') : (toFrom ? document.querySelector('[data-ppb-from]') : null);
          if (other) {
            other.style.removeProperty('display');
            this.ppbSendForm = toPeople ? 'people' : 'from';
            const list = vis(toPeople ? document.querySelector('[data-ppb-send] [data-pp-recip="' + this.recipOpen + '"]') : other);
            next = toPeople ? list[0] : list[list.length - 1];
          }
        }
        if (next) { try { next.focus(); } catch (e) { /* best-effort */ } }
        this._ppbSendReveal();
      },
      /* The field being typed in stays in view above the keyboard bar. The Send page scrolls inside the app shell, and
         the shell only shrinks to the space above the keys AFTER iOS has decided what to reveal (kbBoxH follows the
         keyboard as it rises), so iOS's own reveal measures the old height and can leave the field behind the bar:
         measured on the iPhone 17 Pro simulator, 9 Oct, Apt / suite tapped low on the page went under the bar. So it is
         put back in view here, on focus and again each time the shell's height settles, by the page's own scrollTop
         only (never scrollIntoView, which on iOS scrolls the visual viewport as well). Its label comes with it. */
      _ppbSendReveal() {
        const self = this;
        const run = function () {
          if (self.ppbKb !== 'send') return;
          const a = document.activeElement, page = document.querySelector('.ppb-sendpage');
          if (!a || !page || !a.matches || !a.matches('[data-ppb-send-field]')) return;
          const pr = page.getBoundingClientRect(), r = a.getBoundingClientRect();
          const box = a.closest('label') || a, top = Math.min(r.top, box.getBoundingClientRect().top);
          /* A street field gets room under it for its suggestions (about five, 200 px), so the list is above the
             keyboard bar, not behind it. */
          const PAD = 12 + (a.matches('[data-pp-street], [data-ppb-sender="line1"]') ? Math.min(210, Math.max(0, pr.height - 140)) : 0);
          if (r.bottom > pr.bottom - PAD) page.scrollTop += r.bottom - (pr.bottom - PAD);
          else if (top < pr.top + PAD) page.scrollTop -= (pr.top + PAD) - top;
        };
        this.$nextTick(function () { run(); requestAnimationFrame(run); setTimeout(run, 320); });
      },

      /* ── The panel shown flat (Daniel, 9 Oct) ───────────────────────────────────────────────────────────
         "I'd rather the inside top and note show differently than how they're rendered in 'real world': flat, close
         up against the screen, zoomed in, in the right shape (rounded corners where relevant), so we can maximize
         the space." On a phone, on Inside top and Note, keyboard up or down: the panel faces the screen with no tilt
         and fills the stage, and the rest of the card fades. Its outline is the plane's own (card-stage.js rounds
         the outside corners at R5, today's builder card); the note shows from below the glue band, where the paper
         shows, and the inside top shows whole, hatched band included, until the keyboard is up, when it shows down
         to the band only, so the words are as large as the space allows.
         HOW: the plane keeps its matrix inline (card-stage.js writes it every frame and is not touched); builder-b.css
         overrides it with transform:none !important under [data-ppb-flat], which leaves the plane square to the
         screen at the scene's centre, at its own size; cameraTarget then centres and scales that box. */
      /* ⛔ WHOLE CARD AGAIN, FLAT ONLY WHILE TYPING (Daniel's Pop List, 12 Oct): "The cropped mobile view loses
         orientation, shows an ugly blank white middle, and can't be swiped. Make it slide up and down like the desktop
         version while keeping it clear." On a phone, Inside top and Note now frame the whole card in 3D as desktop
         does (the camera slides between the panels, and a vertical swipe moves between them: ppbSwipeSteps), and the
         flat close-up is kept for the keyboard, when the words want every pixel. The 9 Oct flat view, keyboard down
         too, is one switch away: ?ppflat=1 (remembered in this browser), ?ppflat=0 back. */
      ppbFlatAlways() {
        try {
          const m = /[?&]ppflat=([01])(?:&|$)/.exec(location.search);
          if (m) { if (m[1] === '1') localStorage.setItem('pp_ppflat', '1'); else localStorage.removeItem('pp_ppflat'); }
          return localStorage.getItem('pp_ppflat') === '1';
        } catch (e) { return false; }
      },
      ppbFlatPlane() {
        if (this.stage !== 'builder' || !this.ppbPhone || this.foldedInEditor) return '';
        if (!this.ppbKb && !this._ppbFlatAlways) return '';                        // keyboard down: the whole card, in 3D
        if (!this.ppbKb && this.ppbZoomable() && this.ppbWhole()) return '';       // zoomed out: the whole card, in 3D
        if (this.ppbStep === 'top') return 'top';
        if (this.ppbStep === 'note') return 'note';
        return '';
      },
      // The part of the plane shown, as [top, bottom] shares of its height.
      ppbFlatRange(which) {
        if (which === 'note') return [GLUE_SHARE, 1];
        if (which === 'top' && this.ppbKb === 'top') return [0, 1 - GLUE_SHARE];
        return [0, 1];
      },
      ppbFlatCam(which) {
        const stage = document.querySelector('[data-pp-stage]');
        const plane = document.querySelector('[data-pp-plane="' + (which === 'note' ? 'CoverBottom' : 'CoverTop') + '"]');
        if (!stage || !plane) return null;
        const sw = stage.clientWidth, sh = stage.clientHeight, w = plane.offsetWidth, h = plane.offsetHeight;
        if (!sw || !sh || !w || !h) return null;
        const r = this.ppbFlatRange(which), rh = h * (r[1] - r[0]);
        const scale = Math.min(sw * FLAT_PAD / w, sh * FLAT_PAD / rh);
        const cy = h * ((r[0] + r[1]) / 2 - 0.5);          // the shown part's centre, from the plane's
        return { x: 0, y: -cy * scale, scale: scale };
      },

      /* ── Swiping between pictures (Daniel, 9 Oct: "swipe between pics with finger in addition to the arrows") ──
         On a phone, with a picture selected: a sideways swipe on the stage goes to the next or previous picture, as
         the arrows do. THE RULE: one finger, mostly sideways (more than twice as far across as down), at least
         SWIPE_PX, released within SWIPE_MS, and never while Crop is open, where one finger pans the photo (and
         stageTouchStart already stands down for it); a second finger (a pinch) cancels it, and so does the
         press-and-hold that picks a picture up to move it. Decided on release, so nothing moves under the finger. */
      _ppbSwipeStart(e) {
        this._ppbSwipe = null;
        if (!this.ppbPhone || this.ppbStep !== 'pictures' || this.selectedPhoto === null || this.editSub === 'crop') return;
        if (e.touches && e.touches.length > 1) return;
        /* Photo framing B1: one finger on the selected picture moves the photo in its window; the swipe to the next
           picture is on the stage around it (and the arrows). The design's rule, Builder Photo Framing B1. */
        if (this.frameB1() && e.target && e.target.closest) {
          const panel = e.target.closest('[data-pp-panel]');
          if (panel && Number(panel.getAttribute('data-pp-panel')) === this.selectedPhoto && e.target.closest('.pp-plane__photo')) return;
        }
        const t = e.changedTouches && e.changedTouches[0];
        if (t) this._ppbSwipe = { x: t.clientX, y: t.clientY, t: Date.now() };
      },
      _ppbSwipeMove(e) {
        if (this._ppbSwipe && ((e.touches && e.touches.length > 1) || this.reordering() || this.panning())) this._ppbSwipe = null;
      },
      _ppbSwipeEnd(e) {
        const s = this._ppbSwipe;
        this._ppbSwipe = null;
        const t = e && e.changedTouches && e.changedTouches[0];
        if (!s || !t || this.selectedPhoto === null || this.editSub === 'crop' || this.reordering()) return;
        const dx = t.clientX - s.x, dy = t.clientY - s.y;
        if (Math.abs(dx) < SWIPE_PX || Math.abs(dx) < 2 * Math.abs(dy) || Date.now() - s.t > SWIPE_MS) return;
        this.ppbPick(dx < 0 ? 1 : -1);
        this._ppbSwiped = Date.now();                      // the click that may follow the lift is not a tap
      },

      ppbInit() {
        const self = this;
        try {
          const mq = window.matchMedia(PHONE_MQ);
          self.ppbPhone = mq.matches;
          const on = function (e) { self.ppbPhone = e.matches; self.camNonce++; };
          if (mq.addEventListener) mq.addEventListener('change', on);
          else if (mq.addListener) mq.addListener(on);
        } catch (e) { /* desktop it is */ }
        try {
          const sq = window.matchMedia(SIDE_MQ);
          self.ppbSideFits = sq.matches;
          const onSide = function (e) { self.ppbSideFits = e.matches; self.camNonce++; };
          if (sq.addEventListener) sq.addEventListener('change', onSide);
          else if (sq.addListener) sq.addListener(onSide);
        } catch (e) { /* the tray stays under the card */ }
        // The chat icon beside the ×, shown once Shopify's chat component is defined (no dead button when the app embed
        // is off). Today's header reveals its own with a parse-time script that can run before B's header exists.
        try { if (window.customElements && customElements.whenDefined) customElements.whenDefined('shopify-chat').then(function () { self.ppbChat = true; }); } catch (e) { /* no chat */ }
        /* An address suggestion list opening on Send is scrolled into the page's view (by the page's own scrollTop):
           on desktop a street field low on the page opened its list below the window. */
        try {
          const sendPage = document.querySelector('[data-ppb-send]');
          if (sendPage && window.MutationObserver) new MutationObserver(function (muts) {
            muts.forEach(function (m) { m.addedNodes.forEach(function (n) {
              if (!n.classList || !n.classList.contains('pp-aclist')) return;
              requestAnimationFrame(function () {
                const pr = sendPage.getBoundingClientRect(), r = n.getBoundingClientRect(), over = r.bottom - (pr.bottom - 12);
                if (over > 0) sendPage.scrollTop += over;
              });
            }); });
          }).observe(sendPage, { childList: true, subtree: true });
        } catch (e) { /* the list still works, just maybe below the fold */ }
        document.addEventListener('focusin', function (e) { self._ppbFocusIn(e); });
        document.addEventListener('focusout', function () { self._ppbFocusOut(); });
        this.$watch('section', function (s) { self.ppbFollow(s); });
        this.$watch('cardTool', function (t) { if (t === null) self.ppbTool(); });
        this.$watch('selectedPhoto', function (v) {
          if (!self._ppbSwapping) self.ppbPicPanel = null;
          if (v === null) self.ppfButtons = false;
          self.frameHintSelect(v);
          // B1: the "Edit picture" label leaves with the click that selected, not on the next move (it sat on the hint).
          if (v !== null && self.frameB1()) self.pictureTagOff();
          // A picture tapped on the whole card: zoom back in, with its tools.
          if (v !== null && self.ppbZoomPref === 'out') { self.ppbZoomPref = null; self.$nextTick(function () { self.camNonce++; }); }
        });
        // The zoom belongs to the step it was set on.
        this.$watch('ppbStep', function () {
          if (self.ppbZoomPref !== null) { self.ppbZoomPref = null; self.$nextTick(function () { self.camNonce++; }); }
          self.ppbZoomCue = false;
          self._ppbZoomCueArm();
        });
        this.$watch('stage', function (s) { if (s !== 'builder') self._ppbLanded = false; });
        // The stage grows when the chrome goes and shrinks when it comes back: frame the card again for it.
        this.$watch('ppbKb', function () { self.$nextTick(function () { self.camNonce++; }); });
        this.$watch('kbBoxH', function () { if (self.ppbKb === 'send') self._ppbSendReveal(); });
        this._ppbFlatAlways = this.ppbFlatAlways();
        /* A vertical swipe on the card, on a phone, moves between Inside top and Note (12 Oct), as the camera slides
           between them. Only there, only with no keyboard and no finger already doing something else (a two-finger
           pinch, a horizontal drag). Up goes to Note, down to Inside top. */
        (function () {
          const stage = document.querySelector('[data-pp-stage]');
          if (!stage) return;
          let t = null;
          stage.addEventListener('touchstart', function (e) {
            t = (e.touches.length === 1 && self.ppbPhone && !self.ppbKb && (self.ppbStep === 'top' || self.ppbStep === 'note'))
              ? { x: e.touches[0].clientX, y: e.touches[0].clientY, at: Date.now() } : null;
          }, { passive: true });
          stage.addEventListener('touchmove', function (e) { if (t && e.touches.length > 1) t = null; }, { passive: true });
          stage.addEventListener('touchend', function (e) {
            if (!t || !e.changedTouches.length) return;
            const dx = e.changedTouches[0].clientX - t.x, dy = e.changedTouches[0].clientY - t.y, quick = Date.now() - t.at < 800;
            t = null;
            if (!quick || Math.abs(dy) < 50 || Math.abs(dy) < Math.abs(dx) * 1.5) return;
            if (dy < 0 && self.ppbStep === 'top') self.ppbGo('note');
            else if (dy > 0 && self.ppbStep === 'note') self.ppbGo('top');
          }, { passive: true });
        })();
        // The phone's row of step pills keeps the current step in view (builder-b-stepper.liquid, 12 Oct).
        const follow = function () {
          self.$nextTick(function () {
            const row = document.querySelector('[data-ppb-steprow]'), cur = row && row.querySelector('.is-cur');
            if (!row || !cur || !row.offsetParent) return;
            const x = cur.offsetLeft - (row.clientWidth - cur.offsetWidth) / 2;
            row.scrollTo({ left: Math.max(0, x), behavior: 'smooth' });
          });
        };
        this.$watch('ppbStep', follow);
        this.$watch('stage', follow);
        // The words or the photo, whichever the customer last had, is what Style returns to after Photo.
        this.$watch('card.top && card.top.mode', function (m) { if (m === 'text' || m === 'design') self._ppbWordsMode = m; });
      },
    });

    c.init = function () {
      const out = base.init.apply(this, arguments);
      this.ppbInit();
      return out;
    };

    // ── The inside top's three kinds (see the header) ───────────────────────────────────────────────────
    c.hasTopChoice = function () {
      if (base.hasTopChoice.call(this)) return true;
      return this.ppbTopKind() === 'photo';
    };
    // A photo design has no words: until the customer chooses, it is as designed (today's topOnDesign would start
    // it on the occasion's words, which only a design with a text-free top can carry).
    c.topOnDesign = function () {
      if (this.ppbTopKind() !== 'photo') return base.topOnDesign.apply(this, arguments);
      // Today's makes card.top and drops the caches; then the words it dressed are taken off again.
      const t0 = this.card.top, touched = !!(t0 && t0.touched);
      const out = base.topOnDesign.apply(this, arguments);
      const t = this.card.top;
      if (t && (!touched || t.mode === 'text')) t.mode = 'design';
      this.topRender();
      return out;
    };
    // Under a photo on a photo design: the design's own top, whose glue band the photo leaves bare.
    c._topLoadBase = function () {
      if (this.ppbTopKind() !== 'photo') return base._topLoadBase.apply(this, arguments);
      const url = this.card.design && this.card.design.CoverTop;
      if (!url) return Promise.resolve(null);
      if (this._topBase && this._topBase.url === url) return this._topBase.p;
      const p = new Promise(function (res) {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = function () { res(img); };
        img.onerror = function () { res(null); };
        img.src = url;
      });
      this._topBase = { url: url, p: p };
      return p;
    };
    /* ⛔ THE ORDER STAYS TODAY'S. A photo design kept as designed sends no `_Top` at all, exactly as today's
       builder, which never offered it a choice; `_Top: design` means the same to print, but the property diff
       between the two builders must stay empty. A photo chosen sends `_Top: photo` and `_Photo 5`, which print
       already reads (order.mjs; drawn over the design's own top.png). */
    c.buildCartProperties = function () {
      const props = base.buildCartProperties.apply(this, arguments);
      if (props && this.ppbTopKind() === 'photo' && props._Top === 'design') delete props._Top;
      /* ⭐ THE SENDER (phase 4): on every line of a card sent to someone else or to several people, only when From
         you is complete. Absent means Picturepops to print, so To me and an empty From you send exactly what today's
         builder sends. `_From` is what prints; `_From Ship` is for the cart page to edit later, like `_Ship`.
         ⛔ Not `_Sender name`: print strips the underscore and `Sender name` is a retired key (order.mjs). */
      /* ⭐ WHO IT IS FOR, AS CHOSEN (Daniel, 16 Oct: "record the choices to me, to someone else, to several people
         in the order"): `_Send To` = me / someone / several on every line of the card. Record only: print never
         draws it (order.mjs). The two-week "Did your card arrive?" email reads it for its headline. */
      if (props) {
        const to = { me: 'me', other: 'someone', several: 'several' }[this.sendTo];
        if (to) props['_Send To'] = to;
      }
      const from = props && this.ppbFromValue();
      if (from) {
        const s = this.sender;
        props['_From'] = from;
        props['_From Ship'] = JSON.stringify(s.abroad
          ? { f: s.firstName || '', l: s.lastName || '', a1: '', a2: '', c: '', s: '', z: '', k: '' }
          : { f: s.firstName || '', l: s.lastName || '', a1: s.line1 || '', a2: s.line2 || '',
              c: s.city || '', s: String(s.state || '').toUpperCase(), z: s.zip || '', k: 'US' });
      }
      return props;
    };

    /* Add to cart refuses a half-typed From you, as it refuses a half-typed person: finished or cleared, never sent
       half (an envelope with half a return address is worse than ours). The people's own checks follow in today's
       addToCart. */
    base.addToCart = c.addToCart;
    c.addToCart = function () {
      if (this.stage === 'builder' && this.ppbStep === 'send' && (this.ppbSenderHalf() || (this.ppbFromShown() && this.ppbSenderBad().length))) {
        this.recipTried = true;
        const miss = this.ppbSenderBad().concat(this.ppbSenderMissing());
        const el = miss.length && document.querySelector('[data-ppb-from] [data-ppb-sender="' + miss[0].key + '"]');
        if (el) { try { el.focus(); } catch (e) { /* best-effort */ } }
        return;
      }
      return base.addToCart.apply(this, arguments);
    };

    /* Google Photos failing, said in place in B's words, with what was kept (the design's s02). The specific cases
       (a blocked window, access not given, a sign-in) keep today's sentences: each says what to do. */
    base._googleFailed = c._googleFailed;
    c._googleFailed = function () {
      const out = base._googleFailed.apply(this, arguments);
      const msg = this.errors && this.errors._photos;
      if (msg && /didn.t work just now/.test(msg)) {
        const n = this.photos.length;
        this.errors = { _photos: 'Google Photos didn’t answer. Try again, or upload from this device.' +
          (n ? ' The ' + (n === 1 ? 'photo' : n + ' photos') + ' you picked ' + (n === 1 ? 'is' : 'are') + ' kept.' : '') };
      }
      return out;
    };

    c.noteOnCard = function () { return true; };

    base.stageTouchStart = c.stageTouchStart; base.stageTouchMove = c.stageTouchMove; base.stageTouchEnd = c.stageTouchEnd;
    c.stageTouchStart = function (e) { const out = base.stageTouchStart.apply(this, arguments); this._ppbSwipeStart(e); return out; };
    c.stageTouchMove = function (e) { const out = base.stageTouchMove.apply(this, arguments); this._ppbSwipeMove(e); return out; };
    c.stageTouchEnd = function (e) { const out = base.stageTouchEnd.apply(this, arguments); this._ppbSwipeEnd(e); return out; };
    // A swipe that ends over another picture is not a tap on it.
    base.focusPhoto = c.focusPhoto;
    c.focusPhoto = function (i) {
      if (this._ppbSwiped && Date.now() - this._ppbSwiped < 400) return;
      // B1: a click or tap on the picture already selected is a move or a double-tap on it, never a re-selection.
      if (this.frameB1() && this.selectedPhoto === i && this.ppbStep === 'pictures') return;
      return base.focusPhoto.apply(this, arguments);
    };

    base.cameraTarget = c.cameraTarget;
    c.cameraTarget = function () {
      void this.camNonce; void this.ppbKb;
      const flat = !this.opening && !this.closing && this.ppbFlatPlane();
      const cam = flat && this.ppbFlatCam(flat);
      return cam || base.cameraTarget.apply(this, arguments);
    };

    c.camMode = function () {
      if (this.ppbKb === 'note') return 'note';
      if (this.ppbKb === 'top') return 'top';
      if (this.ppbZoomable()) {
        if (this.ppbWhole()) return 'card';
        if (this.ppbStep === 'look') return 'pics';                            // Part B: the pictures on their border
      }
      if (this.ppbStep === 'top' && this.section === 'card') return 'top';   // a keep design
      if (this.selectedPhoto !== null || this.section === 'photos') {
        return (this.selectedPhoto !== null && this.ppbPhone) ? 'pic' : 'pics';
      }
      return base.camMode.call(this);
    };

    c.framingBoxes = function (mode) {
      const b = this.frameBoxes || {};
      if (mode === 'pics') {
        const four = PANELS.map(function (n) { return b[n]; }).filter(Boolean);
        if (four.length) return four;
      }
      /* The note: the paper it is written on, not the top of the panel the pop-up is glued over (40 of its 122.5 mm,
         under the accordion), as drawn on both devices (Builder Screen's note framing is the paper below the strip).
         Typing on a phone, with the other planes faded, that top would only be blank paper. */
      if (mode === 'note' && b.CoverBottom) {
        const n = b.CoverBottom;
        return [{ cx: n.cx, cy: n.cy + n.h * 0.16, w: n.w, h: n.h * 0.68 }];
      }
      /* The inside top on a desktop: its upper 85%, so the part that shows above the pictures is drawn as large as the
         wide, short stage allows (Builder Screen frames the top at 0.86 of the stage); the glued foot it loses is
         behind the pop-up anyway. A phone, and the keyboard, keep the whole panel, hatched band included, as drawn. */
      if (mode === 'top' && !this.ppbPhone && !this.ppbKb && b.CoverTop) {
        const t = b.CoverTop;
        return [{ cx: t.cx, cy: t.cy - t.h * 0.075, w: t.w, h: t.h * 0.85 }];
      }
      if (mode === 'pic' && this.selectedPhoto !== null && b[PANELS[this.selectedPhoto]]) {
        // Headroom over the picture for the stage's chips (All pictures, Picture 2 of 4), so they sit above its
        // edge rather than on it: an empty box PIC_HEADROOM of the panel tall, stacked on its top.
        const p = b[PANELS[this.selectedPhoto]];
        return [p, { cx: p.cx, cy: p.cy - p.h / 2 - p.h * PIC_HEADROOM / 2, w: p.w / 2, h: p.h * PIC_HEADROOM }];
      }
      return base.framingBoxes.call(this, mode);
    };

    c.uiInset = function () {
      void this.camNonce; void this.section; void this.selectedPhoto; void this.editSub;
      if (this.noteSheetOpen) return base.uiInset.call(this);
      return 0;
    };

    c.leaveToFullCard = function () {
      if (this.stage !== 'builder') return;
      // Send: Escape closes the street suggestions (pp-address.js, which stops the key) and nothing else; leaving
      // the step takes Back (plan §3, "Escape closes the address suggestions, nothing more").
      if (this.ppbStep === 'send') return;
      if (this.ppbSheet) { this.ppbSheet = null; return; }
      blurActive();
      if (this.selectedPhoto !== null) this.closePhoto();
    };

    /* ── FROM THE OPENED CARD TO THE PICTURES, AS ONE MOVE (Daniel, 9 Oct: "a gradual zoom in from the final opened
       state to the zoomed in view of the border with the pics", not a hard cut). B lands on Pictures before the
       unfold, so when the opening hands the camera back (configurator.js _camRelease) the camera's target is already
       the four pictures, and today's hand-back drops the opening's last framing with transitions off: a jump. After
       an opening only, B hands it back the other way round: transition on first, at a slower 800 ms, with the
       opening's last framing still held, and only then the held framing dropped, so the camera eases from exactly
       the frame the opening ended on. Reduced motion, and every other hand-back (Preview's fold), are today's. */
    /* ── INTO THE ENVELOPE (Daniel's Pop List, 12 Oct: "Between step 6 and step 7 the card should close, slide into
       the envelope and then turn into step 7. Try it with Claude first rather than waiting for Barak.") ──────────
       From Preview to Send only, and only with motion allowed: the card folds shut (Preview's own fold), an envelope
       rises behind it, the card slides down into its pocket, the flap closes, and the Send page fades up over it.
       Under the page the card is put back open (_restoreOpenPose), so Back to Preview finds it as it was.
       On by default since 13 Oct (Daniel: "envelope on"); ?ppenvfx=0 turns it off in this browser, ?ppenvfx=1 back.
       Reduced motion skips it. The paper is CSS; the card is the real 3D card.
       Since 18 Oct builder-envelope.js plays Yonatan's rendered envelope instead and falls back to this one (a clip
       that is late or will not play); ?ppenvvid=0 brings this one back in this browser. */
    base.ppbGo = c.ppbGo;
    // The sleeve's paper, loaded before it is needed: on Preview, so the envelope never arrives plain and then textures
    // (Daniel, 12 Oct: "the texture on the envelope didn't load initially ... it needs to render with it").
    // The two baked papers (pp-kraft-front.jpg / -back.jpg) and the card's own cover, decoded before Next is pressed.
    const assetUrl = function (name) {
      const l = document.querySelector('link[href*="builder-b.css"]');
      return l ? l.href.replace(/builder-b\.css[^]*$/, name) : null;
    };
    const decoded = function (u) {
      if (!u) return Promise.resolve();
      const im = new Image();
      im.src = u;
      return (im.decode ? im.decode() : new Promise(function (r) { im.onload = r; im.onerror = r; })).catch(function () {});
    };
    /* Kraft version 2 (12 Oct, builder-b.css "Kraft, version 2"), the default since Daniel's "New envelope looks good"
       the same day; ?ppkraft=1 brings back the film's cut in this browser, ?ppkraft=2 returns to the new paper. */
    const kraftV2 = function () {
      let v2 = true;
      try {
        const m = /[?&]ppkraft=([12])(?:&|$)/.exec(location.search);
        if (m) { if (m[1] === '1') localStorage.setItem('pp_ppkraft', '1'); else localStorage.removeItem('pp_ppkraft'); }
        v2 = localStorage.getItem('pp_ppkraft') !== '1';
      } catch (e) { /* the new paper */ }
      const root = document.querySelector('[data-builder-ui="b"]');
      if (root) { if (v2) root.setAttribute('data-ppkraft', '2'); else root.removeAttribute('data-ppkraft'); }
      return v2;
    };
    kraftV2();
    let kraft = null;
    const kraftReady = function () {
      if (kraft) return kraft;
      const art = document.querySelector('[data-pp-plane="CoverFront"] img');
      const v2 = kraftV2();
      kraft = Promise.all([decoded(assetUrl(v2 ? 'pp-kraft2-front.webp' : 'pp-kraft-front.jpg')),
                           decoded(assetUrl(v2 ? 'pp-kraft2-back.webp' : 'pp-kraft-back.jpg')),
                           decoded(art && (art.currentSrc || art.src))]);
      return kraft;
    };
    c.ppbGo = function (key) {
      const self = this;
      kraftV2();
      let on = true;
      try {
        const m = /[?&]ppenvfx=([01])(?:&|$)/.exec(location.search);
        // ON by default (Daniel, 13 Oct: "envelope on"). ?ppenvfx=0 turns it off in this browser, ?ppenvfx=1 back on.
        if (m) { if (m[1] === '0') localStorage.setItem('pp_ppenvfx', '0'); else localStorage.removeItem('pp_ppenvfx'); }
        on = localStorage.getItem('pp_ppenvfx') !== '0';
      } catch (e) { /* on */ }
      if (on && key === SHOW_STEP) kraftReady();
      const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (key !== 'send' || this.ppbStep !== SHOW_STEP || !on || reduced || this._ppbEnvRun || this.opening || this.stage !== 'builder') {
        return base.ppbGo.apply(this, arguments);
      }
      this._ppbEnvRun = true;
      this._ppbIntoEnvelope().catch(function () {
        // Anything amiss: no half-finished envelope left behind, and the Send page as it would have come.
        document.querySelectorAll('.ppb-envfx, .ppb-envface').forEach(function (e) { e.remove(); });
        if (self.ppbStep !== 'send') base.ppbGo.call(self, 'send');
        const page = document.querySelector('.ppb-sendpage'); if (page) page.classList.remove('is-arriving');
      }).then(function () {
        /* Reopening the hidden 3D card under the page costs ~0.6 s of slow frames (measured 12 Oct): never seen, but felt
           if they tap at once. So it waits for the browser to be idle. */
        return new Promise(function (r) { (window.requestIdleCallback || function (f) { setTimeout(f, 700); })(r, { timeout: 1500 }); });
      }).then(function () {
        const z = document.querySelector('[data-pp-camera]');
        if (z) { z.style.transition = 'none'; z.style.translate = ''; z.style.scale = ''; z.style.opacity = ''; void z.offsetWidth; z.style.transition = ''; }
        if (self._ppbEnvUnhold) { self._ppbEnvUnhold(); self._ppbEnvUnhold = null; }
        const st = document.querySelector('[data-pp-stage]'); if (st) st.classList.remove('is-enveloping');
        try { self._restoreOpenPose(); } catch (e) { /* the card stays closed under the page; Preview reopens it */ }
        self._ppbEnvRun = false;
      });
    };
    /* INTO THE ENVELOPE (Daniel, 12 Oct, three rounds). The envelope is the one in the homepage film: a kraft sleeve
       (pp-kraft.jpg is cut from that film's own frame), portrait, a little taller than the card, open at the top, its
       front cut down in a U thumb-notch, the blue-and-kraft lining showing inside the back. The motion: the card closes
       in place, rises, the sleeve slides up under it, the card goes down into it ("close, rise, and then go down into
       the envelope"), and then the envelope turns 90 degrees and flips over to its front, landing as the envelope on the
       Send page ("the card turns around 90 degrees and maybe even flips around and becomes the card that we see in the
       last step"). The camera is held where Preview had it for the whole move (no zoom in and back out).
       For the turn, the live 3D card gives way to a picture of its own cover inside the sleeve, so the card turns with
       the paper; at the flip, edge-on, the sleeve gives way to a copy of the Send page's envelope, which lands exactly
       on the real one as the page fades in round it. */
    c._ppbIntoEnvelope = function () {
      const self = this;
      const wait = function (ms) { return new Promise(function (r) { setTimeout(r, ms); }); };
      const frame = function () { return new Promise(function (r) { requestAnimationFrame(function () { requestAnimationFrame(r); }); }); };
      const zoom = document.querySelector('[data-pp-camera]'), stage = document.querySelector('[data-pp-stage]');
      const held = { drive: self._camDrive, release: self._camRelease };
      const unhold = function () { self._camDrive = held.drive; self._camRelease = held.release; };
      return (async function () {
        if (!stage || !zoom || zoom.parentNode !== stage) throw new Error('no stage');
        await Promise.race([kraftReady(), wait(1500)]);
        const live = self._camLive && self._camLive();
        if (live) held.drive.call(self, live);
        self._camDrive = function () {};
        self._camRelease = function () { self.camNonce++; };
        self._ppbEnvUnhold = unhold;
        stage.classList.add('is-enveloping');                          // no floor shadow or shading while it travels
        /* Where the Send page's envelope will be, measured now, while nothing moves: the page laid out for a moment,
           unseen. It used to be found by building the page just before the turn, and that work landed on the turn
           and the flip as a stutter (Daniel, 12 Oct). The page itself is now built after the envelope has landed. */
        const page = document.querySelector('.ppb-sendpage');
        let tr = null;
        if (page) {
          const keep = page.getAttribute('style') || '';
          page.style.setProperty('display', 'block', 'important'); page.style.visibility = 'hidden';
          const e0 = page.querySelector('.ppb-env');
          const r0 = e0 && e0.getBoundingClientRect();
          if (r0 && r0.width) tr = { left: r0.left, top: r0.top, width: r0.width, height: r0.height };
          page.setAttribute('style', keep);
        }
        // 1. Close, in place.
        if (!self.cardClosed) self.previewClose();
        const t0 = Date.now();
        while ((!self.cardClosed || self._previewAnim) && Date.now() - t0 < 3000) await wait(40);
        await wait(60);
        const cover = document.querySelector('[data-pp-plane="CoverFront"]');
        const sr = stage.getBoundingClientRect(), cr = cover && cover.getBoundingClientRect();
        if (!cr || !cr.width || !cr.height) throw new Error('no card');
        const W = sr.width, H = sr.height, cx0 = cr.left - sr.left + cr.width / 2, cy0 = cr.top - sr.top + cr.height / 2;
        const k = Math.min(1, (H * 0.44) / cr.height, (W * 0.56) / cr.width);
        const cw = cr.width * k, ch = cr.height * k;
        const at = function (tx, ty) { return [tx - (W / 2 + (cx0 - W / 2) * k), ty - (H / 2 + (cy0 - H / 2) * k)]; };
        const move = function (ms, xy, curve) {
          zoom.style.transition = 'translate ' + ms + 'ms ' + curve + ', scale ' + ms + 'ms ' + curve + ', opacity 200ms linear';
          zoom.style.translate = xy[0].toFixed(1) + 'px ' + xy[1].toFixed(1) + 'px';
          zoom.style.scale = k.toFixed(4);
        };
        const ew = cw * 1.1, eh = ch * 1.18;
        const ecx = W / 2, ebottom = Math.min(H - 8, H * 0.5 + ch * 0.62 + 40), etop = ebottom - eh;
        const restY = etop + ch / 2 + eh * 0.1;
        // 2. Rise.
        const riseY = Math.min(cy0 - ch * 0.12, etop - ch * 0.5 + ch * 0.18);
        move(560, at(ecx, riseY), 'cubic-bezier(.3,0,.2,1)');
        const box = 'left:' + (ecx - ew / 2).toFixed(1) + 'px;top:' + etop.toFixed(1) + 'px;width:' + ew.toFixed(1) + 'px;height:' + eh.toFixed(1) + 'px;';
        const under = document.createElement('div'), over = document.createElement('div');
        under.className = 'ppb-envfx ppb-envfx--under'; over.className = 'ppb-envfx ppb-envfx--over';
        under.setAttribute('aria-hidden', 'true'); over.setAttribute('aria-hidden', 'true');
        under.style.cssText = box; over.style.cssText = box;
        under.innerHTML = '<div class="ppb-envfx__back"><i class="ppb-envfx__lining"></i></div>';
        over.innerHTML = '<div class="ppb-envfx__front"></div>';
        stage.insertBefore(under, zoom);
        stage.appendChild(over);
        await wait(220);
        under.classList.add('is-up'); over.classList.add('is-up');      // the sleeve slides up into place
        await wait(560);
        // 3. Down into the sleeve.
        move(760, at(ecx, restY), 'cubic-bezier(.55,0,.35,1)');
        await wait(860);
        // 4. One piece of paper: the back and a picture of the card's cover go into the front layer, the 3D card goes.
        const art = cover.querySelector('img');
        if (art && art.src) {
          const pic = document.createElement('img');
          pic.className = 'ppb-envfx__card'; pic.alt = ''; pic.src = art.currentSrc || art.src;
          pic.style.cssText = 'left:' + ((ew - cw) / 2).toFixed(1) + 'px;top:' + (restY - ch / 2 - etop).toFixed(1) + 'px;width:' + cw.toFixed(1) + 'px;height:' + ch.toFixed(1) + 'px;';
          over.insertBefore(pic, over.firstChild);
          // The cover's paper goes with it (assets/builder-paper.js), the same layer and mask, turned as the picture is,
          // so the card does not go pure white as it becomes a picture.
          const lay = cover.querySelector(':scope > .rdf-paper.is-on');
          if (lay) {
            const pl = lay.cloneNode(false);
            pl.style.cssText = lay.style.cssText + ';position:absolute;inset:auto;display:block;' + pic.style.cssText + 'transform:rotate(180deg);border-radius:0 0 6% 6% / 0 0 4.6% 4.6%;';
            pic.after(pl);
          }
          try { await pic.decode(); } catch (e) { /* drawn when it can be */ }
        }
        over.insertBefore(under.firstChild, over.firstChild);           // the back, lining and all, under everything
        under.remove();
        zoom.style.transition = 'none'; zoom.style.opacity = '0';
        if (!tr) throw new Error('no envelope on the page');
        await frame();
        const sr2 = stage.getBoundingClientRect();
        const tcx = tr.left - sr2.left + tr.width / 2, tcy = tr.top - sr2.top + tr.height / 2;
        const sc = tr.width / eh;                                         // turned, the sleeve's height is its width
        const turn = 'translate(' + (tcx - ecx).toFixed(1) + 'px,' + (tcy - (etop + eh / 2)).toFixed(1) + 'px) rotate(-90deg) scale(' + sc.toFixed(4) + ')';
        // 6. A quarter turn, onto its side, to where the page's envelope lies.
        over.style.transition = 'transform 640ms cubic-bezier(.45,0,.25,1)';
        over.style.transform = turn;
        await wait(660);
        // 7. Over, edge-on, and the page's own envelope comes round.
        over.style.transition = 'transform 190ms cubic-bezier(.5,0,1,1)';
        over.style.transform = turn + ' scaleY(0.02)';
        await wait(200);
        const env = page.querySelector('.ppb-env');
        const face = env.cloneNode(true);
        [face].concat([].slice.call(face.querySelectorAll('*'))).forEach(function (el) {
          [].slice.call(el.attributes).forEach(function (at) { if (/^(x-|:|@)/.test(at.name)) el.removeAttribute(at.name); });
        });
        face.removeAttribute('style');
        const wrap = document.createElement('div');
        wrap.className = 'ppb-envface';
        wrap.setAttribute('aria-hidden', 'true');
        wrap.style.cssText = 'left:' + (tr.left - sr2.left).toFixed(1) + 'px;top:' + (tr.top - sr2.top).toFixed(1) + 'px;width:' + tr.width.toFixed(1) + 'px;height:' + tr.height.toFixed(1) + 'px;';
        face.style.width = '100%'; face.style.height = '100%'; face.style.margin = '0';
        wrap.appendChild(face);
        stage.appendChild(wrap);
        over.remove();
        void wrap.offsetWidth;
        wrap.classList.add('is-round');
        await wait(260);
        // 8. Only now, with nothing moving, the Send page is built (unseen), and comes up round the envelope, which is
        //    already exactly where its own is.
        page.classList.add('is-arriving');
        base.ppbGo.call(self, 'send');
        await frame();
        page.classList.remove('is-arriving');
        await wait(420);
        wrap.remove();
      })();
    };

    base.finishOpening = c.finishOpening;
    c.finishOpening = function () {
      this._ppbGlide = !(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
      try { return base.finishOpening.apply(this, arguments); } finally { this._ppbGlide = false; }
    };
    base._camRelease = c._camRelease;
    c._camRelease = function () {
      const z = document.querySelector('[data-pp-camera]');
      if (!this._ppbGlide || !z || !z.style.getPropertyValue('--pp-cam-scale')) return base._camRelease.apply(this, arguments);
      this._ppbGlide = false;
      this.camNonce++;
      requestAnimationFrame(function () {
        z.style.setProperty('--pp-cam-dur', '800ms');
        z.style.removeProperty('transition');
        requestAnimationFrame(function () {
          z.style.removeProperty('--pp-cam-x');
          z.style.removeProperty('--pp-cam-y');
          z.style.removeProperty('--pp-cam-scale');
          setTimeout(function () { z.style.removeProperty('--pp-cam-dur'); }, 900);
        });
      });
    };

    c.landInBuilder = function () {
      const out = base.landInBuilder.apply(this, arguments);
      this.ppbLand();
      return out;
    };

    c._armToolbarHint = function () {};

    return c;
  }

  window.PP.builderB = builderB;
})();
